import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useSearchParams } from 'react-router-dom'
import { RouteHighlightOverlay, type RouteHighlightOverlayHandle } from './RouteHighlightOverlay'
import { createCameraAnimator, type CameraAnimator } from '../interaction/cameraAnimator'
import { fetchSchema } from '../schema/fetchSchema'
import type { SchemaData } from '../schema/types'
import { type MetroMapEvent } from '../state/metroMapStore'
import { useMetroMapStore } from '../state/useMetroMapStore'
import { useMapFocusHandle } from '../state/mapFocusContext'
import { useGeolocation } from '../state/geolocationContext'
import { UserLocationDotOverlay, type UserLocationDotOverlayHandle } from './UserLocationDotOverlay'
import { runEffects } from '../state/effects'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import { reportMapReady, type MapMode } from '../analytics/mapSurfaceEvents'
import { reportHealthError, classifyNetworkError, HEALTH_ID } from '../analytics/appHealthEvents'
import { computeContentBBox, type ContentBBox } from '../geometry/contentBBox'
// The map draws pre-tessellated .mmtg binaries (TASK-243).
import { PrecomputedMmtgProvider } from '../geometry/PrecomputedMmtgProvider'
import { getMapBundleSession } from '../mapDelivery/defaultMapBundleSession'
import type { GeometrySet, Theme } from '../geometry/types'
import { buildViewMatrix, CANVAS_SIZE } from '../renderer/viewMatrix'
import { WebGL2Renderer } from '../renderer/WebGL2Renderer'
import { Loader } from './Loader'
import { StationLabelsOverlay, type StationLabelsOverlayHandle } from './StationLabelsOverlay'
import { BubbleOverlay, type BubbleOverlayHandle } from './BubbleOverlay'
import { RouteBubbleOverlay, type RouteBubbleOverlayHandle } from './RouteBubbleOverlay'
import { WalkSegmentOverlay, type WalkSegmentOverlayHandle } from './WalkSegmentOverlay'
import {
  DesktopStationActionsPopup,
  type DesktopStationActionsPopupHandle,
} from './DesktopStationActionsPopup'
import { BREAKPOINT_PX } from './StationCardOrDetailsRouter'
import { useSchema } from './useSchema'
import { lineHexColorFromId } from '../interaction/stationResolver'
import { dayElementIds, nightVisibleIdsWithRoute } from '../state/nightMembership'
import { useNightFrameGeometry } from './useNightFrameGeometry'
import { stationInMode } from '../state/stationInMode'
import { effectiveTheme as computeEffectiveTheme } from '../state/effectiveTheme'
import { extractRouteElementIds } from '../state/buildRouteOverlayInput'
import { extractLineElementIds } from '../lineView/extractLineElementIds'
import { lineDiagramFor } from '../lineView/lineDiagramFor'
import { createLineSegmentHitTester } from '../interaction/lineSegmentHitTester'
import { createCanvasSvgPathContains } from '../interaction/svgPathContains'
import { CITY } from '@city'
import { PARAM_SUGGESTER } from '../state/useSuggesterPanel'
import { shouldDismissSuggesterOnMapTap } from '../state/mapTapSuggesterDismiss'
import { routeFitBottomOcclusionCss } from './routeFitBottomOcclusion'
import { clampCameraPan } from './clampCameraPan'
import { sidebarLeftInsetCssPx } from './sidebarLeftInset'
import type { SuggesterSlot } from '@metro/suggester'
import {
  buildStationLookup,
  clearColorFor,
  computeFocusTarget,
  computeRouteAutoFit,
  DARK_MEDIA_QUERY,
  focusZoomForViewport,
  getSystemTheme,
  hexColorFromSchemaLineId,
  pickViewportSize,
  readInitialViewOverride,
} from './MetroMapCanvas.helpers'

/**
 * Pure-helper module owns: CLEAR_LIGHT / CLEAR_DARK, focus-zoom and
 * viewport-pick formulas, getSystemTheme, clearColorFor,
 * buildStationLookup, hexColorFromSchemaLineId, readInitialViewOverride.
 * See `./MetroMapCanvas.helpers.ts` for the bodies + rationale. Imported
 * above. Lives next door so it's easy to find — but separated out so
 * `react-refresh/only-export-components` stays green on this file.
 *
 * These are UI-layer concerns and intentionally **not** the same as
 * `LIGHT_BG` / `DARK_BG` in `themeRemap.ts`, which are load-bearing for
 * the iOS-port `remapFillColor` logic and must stay at pure `#FFFFFF` /
 * `#1C1C1C` for cross-platform parity with `MetroMapMapAppearance.swift`.
 */

/**
 * Per-event zoom sensitivity for trackpad pinch (`wheel` with `ctrlKey=true`).
 * A pinch gesture streams many small events; the formula is
 * `factor = exp(-deltaY * sensitivity)`, so one pinch event with
 * `deltaY = 5` scales by `exp(-0.05) ≈ 0.95` — ~5% per event, smooth.
 * Discrete `ZOOM_STEP`-style multipliers were too aggressive here.
 */
const PINCH_ZOOM_SENSITIVITY = 0.01
/**
 * Per-event zoom sensitivity for mouse wheel notches. Chrome emits
 * `deltaY = 100` per notch, so `exp(-100 * 0.002) ≈ 0.82` — ~18% per notch,
 * matching the old `ZOOM_STEP = 1.1` feel without being discrete.
 */
const WHEEL_ZOOM_SENSITIVITY = 0.002

/**
 * Free space the camera may show above the map's top edge and below its
 * bottom edge, in CSS px (owner 2026-10-19) — the southernmost stations
 * (Sutton, West Sutton) no longer sit flush on the screen's bottom edge.
 */
const MAP_VERTICAL_PADDING_CSS_PX = 200
/** Zoom multiplier applied when a touch double-tap is detected. */
const DOUBLE_TAP_ZOOM_FACTOR = 2.0
/** Max ms between two taps for them to count as a double-tap. */
const DOUBLE_TAP_MAX_DELAY_MS = 300
/** Max CSS-pixel distance between two taps for them to count as a double-tap. */
const DOUBLE_TAP_MAX_DISTANCE_PX = 30
/**
 * Max CSS-pixel movement from pointerdown to still count as a tap (not a drag).
 * Cross-platform parity — Android `SimpleOnGestureListener` touchSlop default is 8.
 */
const TAP_MAX_MOVEMENT_PX = 8
/** Max ms from pointerdown to pointerup for a tap. If longer, treat as a drag. */
const TAP_MAX_DURATION_MS = 250
/** How far (css px) from a drawn line a tap still opens that line's view. */
const LINE_SEGMENT_TAP_REACH_CSS_PX = 12
/**
 * Min zoom as a multiplier of "contain fit" (full content bbox fits inside
 * the viewport with no cropping). 0.8 gives the user a bit of room to zoom
 * out even past contain — useful on desktop where you may want to see the
 * map with some page margin around it.
 */
const MIN_ZOOM_CONTAIN_FACTOR = 0.8
/** Allow zooming in up to 20x the cover fit — enough to inspect single stations. */
const MAX_ZOOM_COVER_FACTOR = 20

/**
 * Per-second decay factor for pan momentum — `velocity(t) = v0 * friction^t`.
 * At `t = 1s` the velocity is 5% of the initial release velocity. Roughly
 * matches the iOS `UIScrollView.DecelerationRate.fast` feel; the system
 * "normal" rate (`~0.998` per frame) is too slow for a 4096×4096 viewport —
 * a flick would glide for seconds after release.
 *
 * Mirrors the Android constant in
 * `metro-android/feature/metro-map/.../MetroMapGlSurfaceView.kt`
 * (`MOMENTUM_FRICTION_PER_SEC`). Keep in sync manually — these are
 * load-bearing cross-platform feel constants.
 */
/**
 * Session-scoped cold-start latch for the G-1 `map_ready` event. The first
 * map mount of a page-load session is a cold start; any later remount (the
 * canvas normally stays mounted across route swaps, so this is rare) reports
 * `cold_start: false`. Module-level so it survives a component remount within
 * the same session — distinct from the per-mount `hasDrawnOnceRef`.
 */
let mapColdStartConsumed = false

const MOMENTUM_FRICTION_PER_SEC = 0.05
/**
 * Momentum terminates once both axes drop below this velocity (sub-pixel at
 * 60 Hz). Prevents the rAF loop from running indefinitely on near-zero
 * residuals. Mirrors Android `MOMENTUM_STOP_VELOCITY_PX_PER_SEC`.
 */
const MOMENTUM_STOP_VELOCITY_PX_PER_SEC = 30
/**
 * Rolling window for pan velocity sampling. Velocity at release = average
 * movement over the last N milliseconds, not just the single last event —
 * raw last-event velocity is noisy (pointer events can bunch up or thin
 * out at the tail of a drag).
 */
const PAN_VELOCITY_WINDOW_MS = 100

/**
 * Phase E2 interactive metro map canvas.
 *
 * Owns the WebGL2 renderer, one `GeometrySet` per theme, and the viewport
 * state (zoom, offsetX, offsetY). Viewport is held in refs, not React state,
 * so pointer/wheel events at 60+ fps don't trigger React reconciliation — a
 * single `invalidate()` call schedules an rAF that drains `doRender()` once
 * per animation frame.
 *
 * Theme strategy:
 *   - Initial theme read from `prefers-color-scheme` media query
 *   - Matching theme loads **first**, so dark-system users don't see a
 *     light-then-swap flash on cold start
 *   - Other theme preloads in the background for O(1) later swap
 *   - A `matchMedia` listener updates the active theme when the user changes
 *     system appearance; CSS variables retheme the page background via the
 *     same media query, so JS and CSS stay in sync without a `data-theme`
 *     attribute
 *
 * Interaction:
 *   - **1 pointer** (mouse drag or 1-finger touch) → pan
 *   - **2 pointers** (2-finger touch) → pinch zoom + pan, anchored to the
 *     initial pinch center so the world point under the user's fingers
 *     follows them exactly
 *   - **Touch double-tap** → zoom ×2 toward the tap point
 *   - **`wheel` with `ctrlKey=true`** (trackpad pinch on macOS) → continuous
 *     zoom, `factor = exp(-deltaY * PINCH_ZOOM_SENSITIVITY)`
 *   - **`wheel` with `ctrlKey=false` and small fractional `deltaY`** (trackpad
 *     two-finger scroll) → pan
 *   - **`wheel` with `deltaMode=1`, `deltaMode=2`, or `|deltaY| ≥ 50`**
 *     (mouse wheel notches) → zoom toward the cursor, continuous formula
 *   - All zooms clamped to `[fit * 0.5, fit * 20]`
 *
 * StrictMode is handled by a `disposed` flag inside the effect; the double
 * mount in dev releases the first renderer cleanly on first unmount and the
 * second construction owns the canvas from then on.
 *
 * Coverage note: the body of this component owns the WebGL2 renderer, the
 * pointer/wheel event pipeline, the rAF render loop, and several pieces of
 * canvas-attached state (`OffscreenCanvas`, `getContext('webgl2')`,
 * `requestAnimationFrame`, the `window` resize + `visualViewport`
 * resize/scroll listeners, `pointercancel`). jsdom does
 * not implement any of these, so no unit test can reach the body without
 * an enormous fake-browser shim that would itself outsize the production
 * code. Covered instead by Playwright pixel-diff baselines under
 * `tests/visual/` and the dedicated focus-offset / viewport-size tests
 * for the pure helpers above. The `/* istanbul ignore next *\/` marker
 * applies to the entire `MetroMapCanvas` function body.
 */
/* v8 ignore start — WebGL2 + rAF + canvas plumbing, not reachable in jsdom; covered by Playwright tests/visual/ */
export function MetroMapCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rendererRef = useRef<WebGL2Renderer | null>(null)
  const lightSetRef = useRef<GeometrySet | null>(null)
  const darkSetRef = useRef<GeometrySet | null>(null)
  // `themeRef` (the rAF-loop mirror of the resolved render theme) is declared
  // below, AFTER `effectiveTheme` is computed, so it can be seeded from the
  // single resolved theme instead of a system-pref-only read (Bug 7a).

  // After the first successful `renderer.draw()` call we set
  // `canvas.dataset.ready = 'true'` so Playwright visual-regression tests
  // can wait on a deterministic post-render signal instead of a fixed
  // timeout. See tests/visual/helpers/waitForMap.ts.
  const hasDrawnOnceRef = useRef(false)

  // G-1 `map_ready`: wall-clock at mount, used to measure mount →
  // first-interactive-frame `duration_ms`. Captured in the mount effect
  // below (not a ref initializer — `performance.now()` during render is an
  // impure call the react-hooks/purity lint rejects). The first map mount of
  // the session is a cold start; a later remount (route swaps keep the canvas
  // mounted, so this is rare) reports `cold_start: false`.
  const mountStartMsRef = useRef<number | null>(null)

  // Tight bounding rectangle around the actually-drawn content. Computed
  // once per theme on first load and reused across resizes / theme flips.
  // Used by the initial fit algorithm to "cover" the viewport with the
  // content — see `recenter` and `zoomLimits` below. Falls back to a
  // canvas-sized box when the set is empty (shouldn't happen in practice).
  const contentBBoxRef = useRef<ContentBBox | null>(null)

  // Viewport — refs so pan/zoom handlers don't touch React state.
  const zoomRef = useRef(0)
  const offsetXRef = useRef(0)
  const offsetYRef = useRef(0)

  // Single-tap candidate — populated on pointerdown, cleared on first
  // move beyond threshold OR when the double-tap-zoom branch consumes
  // a pointerup. See `onPointerDown` / `onPointerMove` / `endPointer`.
  const tapCandidateRef = useRef<{
    cssX: number
    cssY: number
    t: number
    pointerId: number
  } | null>(null)
  // Deferred dispatch handle. Touch taps dispatch after
  // DOUBLE_TAP_MAX_DELAY_MS so that a pending double-tap-zoom can
  // cancel them before they fire. Mouse/pen taps dispatch immediately.
  const pendingTapDispatchRef = useRef<number | null>(null)

  // §13 map-tap dismissal: snapshot of "is Suggester open + on which
  // slot" and the desktop viewport flag, kept in refs so the tap closure
  // (`fireTap`, which reads only refs at fire time) can decide whether a
  // map tap should also dismiss the open suggester. Synced from the URL
  // `?suggester=` param + the matchMedia viewport below.
  const suggesterSlotRef = useRef<SuggesterSlot | null>(null)
  const isDesktopViewportRef = useRef(false)
  // The active mode's allow-set mirrored into a ref so the ref-only `fireTap`
  // closure can pass it into the `MapTapped` event (hit rejection — D: a
  // station the map does not draw cannot be selected). Null when everything
  // is drawn (London by day). Populated by an effect below.
  const modeVisibleIdsRef = useRef<ReadonlySet<string> | null>(null)
  // What the renderer was last told to filter by — `modeVisibleIds`, or
  // null while the late-night frame is drawn (it holds the night map only).
  // Re-applied after a WebGL context restore.
  const renderNightFilterRef = useRef<ReadonlySet<string> | null>(null)
  // Whether the late-night frame (TASK-1136) is on screen — `__mapDebug`.
  const nightFrameDrawnRef = useRef(false)

  // Schema loaded from `/map-v2/schema.json` — used to resolve tap canvas coords
  // to a station id via `buildStationLookup`. Frontend MMTG pipeline does
  // not load the schema on its own (only binary `.mmtg` assets), so the
  // schema is fetched alongside geometry and stashed here.
  const schemaRef = useRef<SchemaData | null>(null)

  // rAF scheduling.
  const needsRenderRef = useRef(false)
  const rafIdRef = useRef<number | null>(null)
  // WebGL2 context-loss latch (iPadOS Safari GPU-pressure recovery). While
  // the context is lost the rAF loop is paused and `invalidate()` is a no-op;
  // the renderer rebuilds its GL resources on `webglcontextrestored` and the
  // restore callback below flips this false and kicks the loop back on.
  const contextLostRef = useRef(false)

  // TASK-243 Layer 1 (SVG redo): imperative handle to the station-labels
  // SVG overlay. The canvas mutates its CSS transform each rAF tick so
  // the text layer tracks pan/zoom without React re-renders. Null until
  // the overlay finishes fetching its SVG file.
  const labelsOverlayRef = useRef<StationLabelsOverlayHandle | null>(null)
  const routeHighlightOverlayRef = useRef<RouteHighlightOverlayHandle | null>(null)

  // TASK-238.2: imperative handle to the bubble overlay. apply() is called
  // each rAF tick so pan/zoom repositions the bubble without React reconciliation.
  const bubbleOverlayRef = useRef<BubbleOverlayHandle | null>(null)

  // RouteCard A/B map bubbles (origin/destination). Same imperative
  // apply() contract as BubbleOverlay so pan/zoom keeps the A and B
  // bubbles anchored to their stations without React reconciliation.
  const routeBubbleOverlayRef = useRef<RouteBubbleOverlayHandle | null>(null)

  // Inter-station walking-leg pictograms (TASK-273). Same per-frame
  // apply() contract — the overlay redraws the SVG circle attributes
  // each rAF tick so pan/zoom keeps the walking dots anchored to the
  // map canvas without React reconciliation.
  const walkSegmentOverlayRef = useRef<WalkSegmentOverlayHandle | null>(null)

  // Desktop-only From/To action popup that appears below the
  // selectedStation BubbleOverlay when the viewport is >= 768 px. Same
  // imperative apply() contract so pan/zoom keeps the popup anchored
  // without React reconciliation.
  const desktopStationActionsPopupRef = useRef<DesktopStationActionsPopupHandle | null>(null)

  // TASK-391 §3.6: user-location dot overlay. Same per-frame apply()
  // contract as the bubbles so the dot stays anchored to its station
  // without React reconciliation.
  const userLocationDotOverlayRef = useRef<UserLocationDotOverlayHandle | null>(null)

  // TASK-238.3: spring animator for smooth focus-on-station camera transitions.
  // Seeded from current refs at setTarget time so gesture-driven changes since
  // the last focus don't produce a discontinuity.
  const cameraAnimatorRef = useRef<CameraAnimator>(createCameraAnimator())

  // Stable ref to drainRaf so drainRaf can schedule its own continuation
  // without a forward-reference lint error. Updated in an effect after
  // drainRaf is defined so the closure always calls the latest function.
  const drainRafRef = useRef<() => void>(() => {
    /* noop until effect runs */
  })

  // DEV-only accumulator for the per-second `map-frame` debug line
  // (verification instrumentation for the map-FPS perf work): frames and
  // renderer.draw() encode-time summed since the last log. Never read in
  // production builds — the whole consumer is `import.meta.env.DEV`-gated
  // and dead-code-eliminated by Vite.
  const frameLogRef = useRef({ lastLogMs: 0, frames: 0, encodeMsSum: 0 })

  // UDF store for semantic state (theme, readiness, selectedStation,
  // currentMode). Pure `metroMapReducer`, state flows: dispatch(event) →
  // (state, effects). Parity with iOS TCA and Android Elm-style UDF.
  //
  // Transform (zoom, pan) intentionally stays in refs — see the comment at
  // lines 142–146 about 60+ fps reconciliation. Only semantic state is in
  // the store; the render-loop reads transform directly from refs.
  //
  // The store is provided by `<MetroMapStoreProvider>` at the AppShell
  // level so the router layer (URL-as-state wiring for /stations/<slug>)
  // can dispatch into the same store without going through a tap. See
  // wiki/decisions/station-deep-link-routes.md for the rationale.
  const store = useMetroMapStore()
  // TASK-391: shared imperative camera-focus seam — the geolocation
  // controller recenters the map through this handle without mutating
  // store selection state. `null` when no GeolocationController is
  // mounted (isolated canvas tests).
  const mapFocusHandle = useMapFocusHandle()
  // TASK-391 §3.6: live user-location dot target from the geolocation
  // controller. `null` when no controller is mounted or the dot hides.
  const geolocation = useGeolocation()
  const userLocationDotTarget = geolocation?.dotTarget ?? null

  const dispatch = useCallback(
    (event: MetroMapEvent) => {
      const effects = store.dispatch(event)
      runEffects(effects, { logger: (m) => console.log(m), reporter: realMetroAnalyticsReporter })
    },
    [store],
  )

  const theme = useSyncExternalStore(store.subscribe, () => store.getState().theme)
  // TASK-412 M: Night Tube mode. When night, the whole map renders dark
  // (effectiveTheme below) and the geometry is filtered to the night
  // network (modeVisibleIds → renderer.setNightVisibleIds — since Stage-2
  // batching a set CHANGE rebuilds the merged main-pass VBOs once; no
  // re-tessellation, no re-fetch).
  const currentMode = useSyncExternalStore(store.subscribe, () => store.getState().currentMode)
  const lightReady = useSyncExternalStore(store.subscribe, () => store.getState().lightReady)
  const darkReady = useSyncExternalStore(store.subscribe, () => store.getState().darkReady)
  // TASK-1136: a bundle with a late-night frame (New York) draws night mode
  // from its own `*-night.mmtg` set — lines on their own stripes, stations
  // and labels where the night map has them — instead of filtering the day
  // geometry. Loaded the first time night mode is entered; until it is
  // ready, and for a bundle without one (London), the filter path below.
  const nightFrame = useNightFrameGeometry(currentMode === 'night')
  const drawNightFrame = currentMode === 'night' && nightFrame.geometry !== null
  const storedSelectedStation = useSyncExternalStore(
    store.subscribe,
    () => store.getState().selectedStation,
  )
  // RouteCard origin/destination — drives the A/B map bubbles. Read
  // here (not via useRouteSelection hook) to keep the canvas's store
  // wiring uniform with the existing selectedStation pattern.
  const routeSelection = useSyncExternalStore(
    store.subscribe,
    () => store.getState().routeSelection,
  )

  // Routing status (Task 21b) — drives the WebGL route-overlay pass +
  // the WebGL dim toggle. Source-of-truth for "does the user have a route
  // resolved on screen right now". Same useSyncExternalStore pattern as
  // selectedStation / routeSelection so the canvas's store wiring stays
  // uniform across slices.
  const routeStatus = useSyncExternalStore(store.subscribe, () => store.getState().routeStatus)
  const selectedRoute =
    routeStatus.kind === 'results' ? routeStatus.routes[routeStatus.selectedIndex] : null

  // Reactive schema view (separate from `schemaRef`, which is the canvas's
  // private fetch handle for tap resolution). The overlay effect needs to
  // re-run when the schema arrives, which a ref does not provide. Both
  // refs end up populated with the same parsed payload — `<SchemaProvider>`
  // is a single fetch in `<AppShell>` and this hook just reads its context.
  const routeOverlaySchema = useSchema()

  // Pins and bubbles sit where the active mode's map draws the station: the
  // late-night frame moves some stops (TASK-1136, `station.night`).
  const selectedStation = useMemo(
    () => stationInMode(storedSelectedStation, routeOverlaySchema, currentMode),
    [storedSelectedStation, routeOverlaySchema, currentMode],
  )
  const routeFrom = useMemo(
    () => stationInMode(routeSelection.fromStation, routeOverlaySchema, currentMode),
    [routeSelection.fromStation, routeOverlaySchema, currentMode],
  )
  const routeTo = useMemo(
    () => stationInMode(routeSelection.toStation, routeOverlaySchema, currentMode),
    [routeSelection.toStation, routeOverlaySchema, currentMode],
  )

  // Line view (`/<locale>/lines/<slug>`). Shown on the map only while no
  // route lifecycle and no station own it — precedence route > station >
  // line (the station opened from the line view keeps `selectedLine` so
  // closing it returns here).
  const selectedLine = useSyncExternalStore(store.subscribe, () => store.getState().selectedLine)
  const lineViewActive =
    selectedLine !== null && selectedStation === null && routeStatus.kind === 'collapsed'

  // Compute the highlighted element id set for the selected route variant —
  // or, in the line view, for the whole line (same overlay, same look).
  // The SVG <RouteHighlightOverlay> below renders the matching connection /
  // transition / station / text svgPath strings on top of the dim layer.
  // Defined here (before `modeVisibleIds`) because the night draw-time
  // filter unions these ids so the selected route / line survives night
  // mode (see `modeVisibleIds` below).
  const highlightedElementIds = useMemo<ReadonlySet<string>>(() => {
    if (routeOverlaySchema === null) return new Set()
    if (selectedRoute !== null) return extractRouteElementIds(selectedRoute, routeOverlaySchema)
    if (lineViewActive) return extractLineElementIds(routeOverlaySchema, selectedLine.lineId)
    return new Set()
  }, [selectedRoute, routeOverlaySchema, lineViewActive, selectedLine])

  // Segment tap → line view. Built once per schema; only lines that have a
  // line view in this city (`lineDiagramFor`) can be hit. Read from the
  // ref-only `fireTap` closure, hence the ref.
  const lineSegmentHitTesterRef = useRef<ReturnType<typeof createLineSegmentHitTester> | null>(null)
  useEffect(() => {
    lineSegmentHitTesterRef.current =
      routeOverlaySchema === null
        ? null
        : createLineSegmentHitTester(
            routeOverlaySchema,
            (lineId) => lineDiagramFor(routeOverlaySchema, lineId, CITY.lineDiagrams) !== null,
            createCanvasSvgPathContains(),
          )
  }, [routeOverlaySchema])

  // TASK-412 M (option i). In night mode the map forces the dark palette
  // regardless of the light/dark `theme` (which tracks OS pref); normal
  // mode uses `theme` as before.
  const effectiveTheme: Theme = computeEffectiveTheme({ theme, currentMode })
  // Bug 7a: the SINGLE resolved render theme for the rAF draw loop. Held in
  // a ref because `drainRaf` (a `useCallback([])`) can't read React state.
  // BOTH the GL clear color (`clearColorFor(themeRef.current)`) and the
  // geometry VBO select from THIS value, so the two map layers can never
  // diverge — e.g. night mode must give the dark geometry a dark clear
  // color, not a system-pref-only clear color. Seeded from the initial
  // `effectiveTheme` (store `theme` folded with night mode) so even the very
  // first frame is correct, and kept in lockstep by the geometry-swap effect
  // below (`themeRef.current = effectiveTheme`).
  const themeRef = useRef<Theme>(effectiveTheme)
  // System color-scheme preference as a reactive, ALWAYS-LIVE value.
  // `useSyncExternalStore` re-reads its snapshot on every commit, so a stale
  // initial `matchMedia` read — an iPad Safari WebKit quirk where the first
  // synchronous read reports light although the system is dark AND never
  // fires a `change` event — self-heals on the next render instead of
  // sticking forever the way the old mount-time `themeRef` seed did. Mirrored
  // into the UDF store by the effect further below (Bug 7a).
  const systemTheme = useSyncExternalStore(
    (callback) => {
      const mql = window.matchMedia(DARK_MEDIA_QUERY)
      mql.addEventListener('change', callback)
      return () => mql.removeEventListener('change', callback)
    },
    getSystemTheme,
    () => 'light' as Theme, // SSR fallback (unused; metro-frontend is CSR)
  )
  // G-1 `map_ready.mode`: the effective map theme as the catalog enum
  // (`light` | `dark` | `night`). Held in a ref because the first-frame emit
  // lives in the rAF draw loop (`drainRaf`, a `useCallback([])`) which can't
  // read React state directly. Night takes precedence over the light/dark
  // OS theme; otherwise it is the OS-pref theme.
  const mapMode: MapMode = currentMode === 'night' ? 'night' : theme
  const mapModeRef = useRef<MapMode>(mapMode)
  useEffect(() => {
    mapModeRef.current = mapMode
  }, [mapMode])
  // G-1 `map_ready` mount marker. Captured in the earliest mount effect
  // (impure `performance.now()` is not allowed during render). Set once;
  // re-running guarded so a dependency-less effect that React re-invokes in
  // StrictMode dev double-mount does not reset an already-captured time.
  useEffect(() => {
    if (mountStartMsRef.current === null) mountStartMsRef.current = performance.now()
  }, [])
  // The active mode's allow-set for the draw-time filter. Memoised on the
  // schema; the renderer push effect below applies it when the mode flips.
  //
  // By day it is `dayElementIds`: null (draw everything) unless the bundle
  // has night-only elements (a late-night frame city, TASK-1136), which the
  // day files carry but the day map hides.
  //
  // The SELECTED route's highlighted element ids are UNIONED into the
  // allow-set so the route survives night mode: legs on non-night lines
  // (Elizabeth / Thameslink / Lioness) are night-filtered out of the base
  // geometry, which would otherwise erase half the highlighted route. The
  // union keeps those exact route segments drawn even though the rest of
  // their line stays hidden.
  const modeVisibleIds = useMemo<ReadonlySet<string> | null>(() => {
    if (routeOverlaySchema === null) return null
    return currentMode === 'night'
      ? nightVisibleIdsWithRoute(routeOverlaySchema, highlightedElementIds)
      : dayElementIds(routeOverlaySchema)
  }, [currentMode, routeOverlaySchema, highlightedElementIds])

  // Desktop viewport (>= 768 px) gates the DesktopStationActionsPopup.
  // The mobile (<768 px) branch already shows From/To via the
  // StationCard carousel — the popup is strictly the desktop-only
  // equivalent. The check is read here so the parent (the canvas) owns
  // the policy and the popup itself stays oblivious.
  const isDesktopViewport = useSyncExternalStore(
    (callback) => {
      const mql = window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`)
      mql.addEventListener('change', callback)
      return () => mql.removeEventListener('change', callback)
    },
    () => window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`).matches,
    () => false, // SSR fallback (not used since metro-frontend is CSR)
  )

  // §13 map-tap dismissal wiring. The open Suggester slot lives in the
  // URL (`?suggester=`), shared with `useSuggesterPanel`; mirror it (and
  // the desktop flag) into refs so the ref-only `fireTap` closure can read
  // the live value at tap time. `closeSuggester` drops the param with
  // `replace: true` (matching the panel hook's `close`), which retains the
  // route pills (store state, untouched) and clears the transient query
  // (reset on the slot→null transition inside the panel hook).
  const [searchParams, setSearchParams] = useSearchParams()
  const suggesterSlotParam = searchParams.get(PARAM_SUGGESTER)
  const suggesterSlot: SuggesterSlot | null =
    suggesterSlotParam === 'from' || suggesterSlotParam === 'to' ? suggesterSlotParam : null
  const closeSuggester = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.delete(PARAM_SUGGESTER)
        return params
      },
      { replace: true },
    )
  }, [setSearchParams])
  const closeSuggesterRef = useRef(closeSuggester)
  // Sync the §13 tap-time inputs into refs from an effect (not during
  // render) so the ref-only `fireTap` closure reads the latest values
  // without re-binding the pointer handlers. Same pattern as `themeRef`.
  useEffect(() => {
    suggesterSlotRef.current = suggesterSlot
    isDesktopViewportRef.current = isDesktopViewport
    closeSuggesterRef.current = closeSuggester
    modeVisibleIdsRef.current = modeVisibleIds
  }, [suggesterSlot, isDesktopViewport, closeSuggester, modeVisibleIds])
  // Desktop From/To popup gated additionally on routeStatus being
  // collapsed: once a route is built (loading / results / empty /
  // error) the user has finalised the route and the From/To buttons
  // would be redundant — they already see the result panel in the
  // sidebar. Tapping a station in built-route mode should just focus
  // the station, not let the user hijack the route slots from under
  // the result. TASK-275 user feedback 2026-05-06.
  //
  // Latch behaviour (TASK-275 QA finding #13): once the popup opens
  // for a given selectedStation id, hold `enabled = true` for the
  // lifetime of that selection even if `routeStatus` flips off
  // `collapsed` mid-tap (e.g. a queued route compute lands in the same
  // render batch as `StationSelected`). Without the latch the popup
  // would mount + immediately unmount before the user sees it. The
  // latch resets when selectedStation returns to null.
  const desktopActionsEnabledRaw = isDesktopViewport && routeStatus.kind === 'collapsed'
  const [latchedSelectionId, setLatchedSelectionId] = useState<string | null>(null)
  useEffect(() => {
    if (selectedStation === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: latch must clear when selection returns to null (TASK-275 #13)
      if (latchedSelectionId !== null) setLatchedSelectionId(null)
      return
    }
    if (desktopActionsEnabledRaw && latchedSelectionId !== selectedStation.id) {
      setLatchedSelectionId(selectedStation.id)
    }
  }, [selectedStation, desktopActionsEnabledRaw, latchedSelectionId])
  const desktopActionsEnabled =
    selectedStation !== null && latchedSelectionId === selectedStation.id
      ? true
      : desktopActionsEnabledRaw

  // Pulled out so both the mount effect and the theme effect can schedule it.
  const drainRaf = useCallback(() => {
    rafIdRef.current = null
    if (!needsRenderRef.current) return
    needsRenderRef.current = false
    const renderer = rendererRef.current
    const canvas = canvasRef.current
    if (renderer === null || canvas === null) return
    // Context lost — the GL context is dead until `webglcontextrestored`.
    // Skip the frame and stop self-scheduling; the restore path resumes.
    if (contextLostRef.current) return

    // TASK-238.3: tick the camera spring BEFORE reading refs for the view
    // matrix so the renderer + overlays all see the animated transform.
    const nowSec = performance.now() / 1000
    const animator = cameraAnimatorRef.current
    if (!animator.isSettled) {
      const tick = animator.tick(nowSec)
      zoomRef.current = tick.zoom
      offsetXRef.current = tick.panX
      offsetYRef.current = tick.panY
      if (!tick.settled) {
        // Self-sustain: schedule another frame. Use drainRafRef so the
        // closure doesn't forward-reference the not-yet-declared drainRaf
        // const (lint: no-use-before-define). drainRafRef is populated
        // immediately after the useCallback below.
        needsRenderRef.current = true
        if (rafIdRef.current === null) {
          rafIdRef.current = requestAnimationFrame(drainRafRef.current)
        }
      }
    }

    // Every camera move (drag, pinch, wheel, fling, spring) ends on the map:
    // the content never leaves past a screen edge or under the route panel.
    const bbox = contentBBoxRef.current
    if (bbox !== null && zoomRef.current > 0) {
      const clamped = clampCameraPan({
        panX: offsetXRef.current,
        panY: offsetYRef.current,
        zoom: zoomRef.current,
        bbox,
        viewportPxW: canvas.width,
        viewportPxH: canvas.height,
        leftInsetPx:
          sidebarLeftInsetCssPx(isDesktopViewportRef.current) * (window.devicePixelRatio || 1),
        verticalPaddingPx: MAP_VERTICAL_PADDING_CSS_PX * (window.devicePixelRatio || 1),
      })
      offsetXRef.current = clamped.panX
      offsetYRef.current = clamped.panY
    }

    const viewMatrix = buildViewMatrix(
      zoomRef.current,
      offsetXRef.current,
      offsetYRef.current,
      canvas.width,
      canvas.height,
    )
    const encodeStartMs = import.meta.env.DEV ? performance.now() : 0
    // Stage-3 tile culling: hand the renderer the same pan/zoom the view
    // matrix was built from, so tiled buckets draw only the visible tiles.
    renderer.draw(viewMatrix, clearColorFor(themeRef.current), {
      zoom: zoomRef.current,
      offsetX: offsetXRef.current,
      offsetY: offsetYRef.current,
    })
    if (import.meta.env.DEV) {
      // Per-second `map-frame` debug line — how the user and QA see each
      // perf stage actually working (Stage-1 baseline: draws≈1931).
      const log = frameLogRef.current
      const nowMs = performance.now()
      log.frames += 1
      log.encodeMsSum += nowMs - encodeStartMs
      if (nowMs - log.lastLogMs >= 1000) {
        const { draws, verts } = renderer.lastFrameStats
        console.debug(
          `map-frame: draws=${draws} encode=${(log.encodeMsSum / log.frames).toFixed(2)}ms verts=${verts}`,
        )
        log.lastLogMs = nowMs
        log.frames = 0
        log.encodeMsSum = 0
      }
    }
    // Bug 5: the canvas's physical pixel buffer is sized with
    // `(devicePixelRatio || 1) * visualViewport.scale` (see
    // syncCanvasSize) so that browser-level pinch keeps the canvas
    // crisp. The overlays must use the SAME effective DPR or their
    // CSS transforms / left-top derivations diverge from the canvas
    // viewport — visible as "texts/bubble drift" after a resize or
    // browser pinch. Keep the two reads in sync here.
    const effectiveDpr = (window.devicePixelRatio || 1) * (window.visualViewport?.scale ?? 1)
    // Keep the label overlay locked to the WebGL viewport. Same rAF
    // tick, same input state — no React render. No-op when the overlay
    // hasn't mounted / fetched yet. The physical viewport size feeds the
    // Stage-3W raster planner (windowed re-raster at extreme zoom).
    labelsOverlayRef.current?.apply(
      zoomRef.current,
      offsetXRef.current,
      offsetYRef.current,
      effectiveDpr,
      canvas.width,
      canvas.height,
    )
    // RouteHighlightOverlay shares the same per-frame apply() contract —
    // svg overlay above the dim layer carrying the route's geometry +
    // station texts.
    routeHighlightOverlayRef.current?.apply(
      zoomRef.current,
      offsetXRef.current,
      offsetYRef.current,
      effectiveDpr,
    )
    // TASK-238.2: reposition the bubble overlay each frame so pan/zoom
    // keeps the bubble anchored to its station without React reconciliation.
    bubbleOverlayRef.current?.apply(
      zoomRef.current,
      offsetXRef.current,
      offsetYRef.current,
      effectiveDpr,
    )
    // RouteCard A/B bubbles share the same per-frame apply() contract.
    // No-op when the overlay is unmounted or no slot is selected.
    routeBubbleOverlayRef.current?.apply(
      zoomRef.current,
      offsetXRef.current,
      offsetYRef.current,
      effectiveDpr,
    )
    // Walk-segment overlay (TASK-273) — pictogram dots for inter-station
    // walking legs. Same per-frame contract; no-op while no route is
    // selected or the selected route has no walking legs.
    walkSegmentOverlayRef.current?.apply(
      zoomRef.current,
      offsetXRef.current,
      offsetYRef.current,
      effectiveDpr,
    )
    // Desktop From/To action popup — same per-frame apply() contract.
    // No-op while the popup is unmounted (mobile viewport, or no
    // station selected).
    desktopStationActionsPopupRef.current?.apply(
      zoomRef.current,
      offsetXRef.current,
      offsetYRef.current,
      effectiveDpr,
    )
    // TASK-391 §3.6: keep the user-location dot anchored to its
    // nearest-station canvas position. No-op while the dot is hidden.
    userLocationDotOverlayRef.current?.apply(
      zoomRef.current,
      offsetXRef.current,
      offsetYRef.current,
      effectiveDpr,
    )
    // Deterministic "first frame drawn" signal for visual-regression
    // tests (see tests/visual/helpers/waitForMap.ts). Only flips once.
    if (!hasDrawnOnceRef.current) {
      hasDrawnOnceRef.current = true
      canvas.dataset.ready = 'true'
      // G-1 `map_ready`: first interactive frame reached. Measure mount →
      // first-frame `duration_ms`; the first map mount of the session is the
      // cold start (the module latch survives a remount). Mirror iOS /
      // Android `map_ready { duration_ms, cold_start, mode }`. Reporter is
      // production-gated + fail-safe — a perf emit never blocks the frame.
      const coldStart = !mapColdStartConsumed
      mapColdStartConsumed = true
      // mountStartMsRef is set in the mount effect, which commits before the
      // first paint (and thus before the first rAF frame); the `?? now`
      // fallback yields duration 0 only in the impossible case it is unset.
      const startMs = mountStartMsRef.current ?? performance.now()
      reportMapReady(realMetroAnalyticsReporter, {
        durationMs: performance.now() - startMs,
        coldStart,
        mode: mapModeRef.current,
      })
      // TASK-1123: the map bundle drew its first frame — confirm it, clean
      // up old bundles and look for a newer one (spec §3.6–3.7).
      void getMapBundleSession().confirmFirstFrame()
    }

    // Read-only state handle for interaction tests. See
    // tests/visual/interactions/*.spec.ts. ~30 bytes minified, no
    // secrets — left in production builds for simplicity and to keep
    // live-site debugging trivial (`$0.__mapDebug` in devtools).
    ;(window as unknown as { __mapDebug?: unknown }).__mapDebug = {
      zoom: zoomRef.current,
      offsetX: offsetXRef.current,
      offsetY: offsetYRef.current,
      theme: themeRef.current,
      nightFrame: nightFrameDrawnRef.current,
    }
  }, [])

  // Keep drainRafRef in sync so the animator's self-scheduling inside
  // drainRaf can call itself without a forward-reference lint error.
  useEffect(() => {
    drainRafRef.current = drainRaf
  }, [drainRaf])

  const invalidate = useCallback((): void => {
    // Don't schedule frames while the GL context is lost — the restore
    // callback re-invalidates once the context (and geometry) are back.
    if (contextLostRef.current) return
    needsRenderRef.current = true
    if (rafIdRef.current !== null) return
    rafIdRef.current = requestAnimationFrame(drainRaf)
  }, [drainRaf])

  // TASK-238.3: animate the viewport to centre the selected station. Pan
  // formula mirrors Android: place the station at viewport centre at the
  // target zoom. offsetX/Y are in unscaled canvas space (same convention
  // as Android — see screenToCanvas from TASK-238.1).
  const focusOn = useCallback(
    (snapshot: { canvasX: number; canvasY: number }, options?: { instant?: boolean }): void => {
      const canvas = canvasRef.current
      if (canvas === null) return
      const currentZoom = zoomRef.current
      if (currentZoom <= 0) return // canvas not yet sized — fit-to-screen hasn't run
      // Zoom IN to the viewport-appropriate target, or respect the user's
      // current zoom if they're already zoomed in further. Never zoom OUT
      // as a side-effect of a tap.
      // Shift focus 100 CSS px above viewport centre so the station mark
      // (and bubble) sits in the upper-middle of the screen rather than
      // dead-centre. Better UX: the station gets visual breathing room
      // above, and the bottom card overlay stops competing with the
      // selected mark for the centre of attention. 100 CSS px → 100 * dpr
      // canvas-physical px → /targetZoom canvas-coord units. Adding to
      // panY pushes the camera DOWN, which moves the station UP on screen.
      const dpr = (window.devicePixelRatio || 1) * (window.visualViewport?.scale ?? 1)
      // Cross-platform parity §5.1: the floating route panel covers the
      // left of the map, so the station is centred in the band right of it
      // (`sidebarLeftInsetCssPx`).
      const leftInsetCssPx = sidebarLeftInsetCssPx(isDesktopViewport)
      const {
        zoom: targetZoom,
        panX: rawPanX,
        panY: rawPanY,
      } = computeFocusTarget({
        canvasX: snapshot.canvasX,
        canvasY: snapshot.canvasY,
        currentZoom,
        focusZoom: focusZoomForViewport(),
        viewportPxW: canvas.width,
        viewportPxH: canvas.height,
        dpr,
        upwardOffsetCssPx: 100,
        leftInsetCssPx,
      })

      // Clamp pan so the camera stays on the map — the left edge counted
      // from the route panel. Without the panel term the clamp undid the
      // focus inset for a station near the west edge: "my location" at
      // Heathrow centred it right of the panel, then the clamp slid it
      // back under the panel. Without any clamp, a station near the bbox
      // edge (Camden Road + focusZoom 3) exposed the GL clear colour above
      // the map (TASK-275 2026-05-05).
      const bbox = contentBBoxRef.current
      let targetPanX = rawPanX
      let targetPanY = rawPanY
      if (bbox !== null) {
        const clamped = clampCameraPan({
          panX: rawPanX,
          panY: rawPanY,
          zoom: targetZoom,
          bbox,
          viewportPxW: canvas.width,
          viewportPxH: canvas.height,
          leftInsetPx: leftInsetCssPx * dpr,
          verticalPaddingPx: MAP_VERTICAL_PADDING_CSS_PX * dpr,
        })
        targetPanX = clamped.panX
        targetPanY = clamped.panY
      }

      // §3.2 launch recenter: snap the camera directly with no spring. A
      // spring on cold load reads as a jarring lurch and — because it is
      // still mid-flight when visual-regression tests screenshot the
      // default view — produces non-deterministic captures. Seeding the
      // animator at the target keeps it settled so subsequent gestures
      // pick up cleanly.
      if (options?.instant === true) {
        zoomRef.current = targetZoom
        offsetXRef.current = targetPanX
        offsetYRef.current = targetPanY
        cameraAnimatorRef.current.seed(targetZoom, targetPanX, targetPanY)
        invalidate()
        return
      }

      cameraAnimatorRef.current.seed(zoomRef.current, offsetXRef.current, offsetYRef.current)
      cameraAnimatorRef.current.setTarget({
        zoom: targetZoom,
        panX: targetPanX,
        panY: targetPanY,
        nowSec: performance.now() / 1000,
      })
      invalidate() // kick off the rAF loop
    },
    [invalidate, isDesktopViewport],
  )

  // Live mirror of `focusOn` for the mount-scoped geometry effect: the
  // callback's identity changes with `isDesktopViewport`, and putting it
  // in that effect's deps would tear down the renderer on a viewport
  // flip. The ref always holds the current version.
  const focusOnRef = useRef(focusOn)
  useEffect(() => {
    focusOnRef.current = focusOn
  }, [focusOn])

  // Bug 7a: mirror the always-live system color-scheme preference into the
  // UDF store. Runs on mount — correcting a store `theme` that was seeded
  // from a STALE initial `matchMedia` read (iPad Safari reports light while
  // the system is dark and never fires a `change` event, so the old
  // change-only listener could never recover) — and again whenever the OS
  // preference actually changes. Because `systemTheme` is sourced through
  // `useSyncExternalStore`, its value is re-read on every commit and settles
  // to the correct pref shortly after mount, so this dispatch corrects the
  // store even when no `change` event ever fires. The reducer no-ops when the
  // theme is unchanged, so a redundant same-value dispatch is free. The store
  // `theme` (folded with night mode by `effectiveTheme`) is the single source
  // for both the geometry VBO and the GL clear color.
  useEffect(() => {
    dispatch({ kind: 'SystemThemeChanged', theme: systemTheme })
  }, [systemTheme, dispatch])

  // When `theme` flips (initial mount or system change), swap the renderer's
  // active VBO to the matching GeometrySet if it's already loaded, and
  // invalidate to repaint with the new clear color.
  //
  // Edge case: if the target theme hasn't preloaded yet (rare — only during
  // the ~300–500 ms window between initial load and background preload), we
  // keep the current geometry on-GPU. The CSS background flips instantly via
  // its own media query, so there's a brief visual mismatch until the other
  // set arrives — acceptable for an edge case that auto-heals.
  //
  // Depends on `lightReady` / `darkReady` so the effect re-runs when a set
  // that wasn't loaded at the time of a previous theme flip finally arrives.
  useEffect(() => {
    themeRef.current = effectiveTheme
    const renderer = rendererRef.current
    const frameSet = drawNightFrame ? nightFrame.geometry : null
    const nextSet =
      frameSet ?? (effectiveTheme === 'dark' ? darkSetRef.current : lightSetRef.current)
    if (renderer !== null && nextSet !== null) {
      renderer.uploadGeometry(nextSet)
    }
    // TASK-412 M: apply / clear the night-service visibility filter. A set
    // CHANGE rebuilds the renderer's merged main-pass VBOs once (Stage-2
    // batching); a same-reference re-apply — this effect re-runs on ready
    // flags too — is a renderer-side no-op. The late-night frame is the
    // night map already: nothing to filter.
    const filter = frameSet === null ? modeVisibleIds : null
    renderNightFilterRef.current = filter
    nightFrameDrawnRef.current = frameSet !== null
    if (renderer !== null) {
      renderer.setNightVisibleIds(filter)
    }
    needsRenderRef.current = true
    if (rafIdRef.current === null) {
      rafIdRef.current = requestAnimationFrame(drainRaf)
    }
  }, [
    effectiveTheme,
    modeVisibleIds,
    drainRaf,
    lightReady,
    darkReady,
    drawNightFrame,
    nightFrame.geometry,
  ])

  // TASK-238.3: focus the camera on the selected station whenever the
  // selection changes to a non-null value. Deselect (null) produces no
  // camera motion per spec §3.4.
  useEffect(() => {
    if (selectedStation !== null) {
      focusOn(selectedStation)
    }
  }, [selectedStation, focusOn])

  // TASK-391: publish `focusOn` into the shared geolocation focus handle
  // so the my-location controller can recenter the camera. Unregistered
  // on unmount so a stale callback is never invoked.
  useEffect(() => {
    if (mapFocusHandle === null) return
    mapFocusHandle.register(focusOn)
    return () => {
      mapFocusHandle.register(null)
    }
  }, [mapFocusHandle, focusOn])

  // On phone, pan the camera onto the From station whenever the From id
  // changes to a new non-null value (URL deep-link on initial load, OR
  // mid-session swap to a different From). We track the last focused id
  // rather than a fire-once boolean so re-selecting a different From
  // after clearing the previous one still re-focuses. Deselect
  // (`selectedStation` → null) does NOT re-trigger because the effect
  // only runs when the From id itself changes — clearing a selection
  // while From stays the same is a no-op here.
  // TASK-275 2026-05-06; QA finding #10.
  const fromStation = routeFrom
  const lastFocusedFromIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (selectedStation !== null) return
    if (fromStation === null) {
      lastFocusedFromIdRef.current = null
      return
    }
    if (lastFocusedFromIdRef.current === fromStation.id) return
    if (typeof window === 'undefined' || window.innerWidth >= BREAKPOINT_PX) return
    lastFocusedFromIdRef.current = fromStation.id
    focusOn({ canvasX: fromStation.canvasX, canvasY: fromStation.canvasY })
  }, [fromStation, selectedStation, focusOn])

  // Dim layer driven by DOM, not WebGL. Architecture difference from
  // mobile: iOS and Android render station labels INSIDE the engine
  // (part of MMTG geometry), so a Metal/GL dim pass after all map
  // passes naturally covers `map → text` together. On web the labels
  // live in a DOM overlay (`StationLabelsOverlay`), which is layered
  // above the canvas and would NOT be dimmed by a WebGL pass. Using a
  // DOM-level dim `<div>` with z-index between labels (z=1) and the
  // bubble (z=3) achieves the mobile layering: map → text → dim → bubble.
  //
  // Bonus: a CSS transition on opacity animates the ramp without any
  // keep-alive loop. The previous WebGL approach had the same race the
  // Android port hit — `RENDERMODE_WHEN_DIRTY` means the animator's
  // `tick()` wouldn't fire unless something else invalidated. A single
  // `invalidate()` at selection change produced one frame of partial
  // alpha, then the animator stalled visually because the user hadn't
  // panned. CSS transitions sidestep that entirely.
  // Single DOM dim layer covers BOTH the station-focus surface AND the
  // route-results surface. The route-highlight overlay (svg, mounted at
  // z=3 above the dim) carries the bright pixels for the selected route
  // on top.
  // The line view takes the route dim too — the line reads like a built
  // route on top of a strongly-subdued map.
  const routeDimActive =
    routeStatus.kind === 'results' ||
    routeStatus.kind === 'empty' ||
    routeStatus.kind === 'error' ||
    lineViewActive
  // Dim layer always DOM-mounted (no flash on mount/unmount); opacity
  // drives visual. Priority route > station > idle:
  //   - route lifecycle (results / empty / error): the deepest dim so the
  //     highlighted route reads clearly against a strongly-subdued map.
  //     The scrim colour is the EFFECTIVE theme's background (`#1C1C1C`
  //     dark / `#FFFFFF` light, see below), so light needs a HIGHER
  //     opacity than dark to subdue the map by the same perceived amount.
  //     0.72 dark / 0.85 light — matches iOS/Android route focus-dim
  //     (tester "deepen the route dim" request; all three platforms align).
  //   - selected station (StationCard visible): 0.4 — matches iOS/Android
  //     station focus-dim (parity).
  //   - idle: 0.01 baseline — layer effectively invisible, transition-ready.
  // Mobile RouteCard collapsed (just pills) does NOT trigger dim — pills
  // float over a clear-strength map per spec.
  const routeDimOpacity = effectiveTheme === 'dark' ? 0.72 : 0.85
  const dimTargetOpacity = routeDimActive ? routeDimOpacity : selectedStation !== null ? 0.4 : 0.01
  // M: the dim layer's backing follows the EFFECTIVE theme so night mode
  // (forced dark) shows the dark backing, not the OS-pref light one.
  const dimBackgroundColor = effectiveTheme === 'dark' ? '#1C1C1C' : '#FFFFFF'

  // `highlightedElementIds` is computed earlier (just after
  // `routeOverlaySchema`) so the night draw-time filter can union it — see
  // that memo for the route-survives-night-mode rationale.

  // Stations the camera fits: the selected route's, or in the line view the
  // whole line's. Null when neither is on screen.
  const fitStationIds = useMemo<ReadonlySet<string> | null>(() => {
    if (selectedRoute !== null) {
      // Ride leg stops + walk / transfer endpoints — no need to include
      // connection or transition ids; we just need the geometric bbox.
      const stationIds = new Set<string>()
      for (const leg of selectedRoute.legs) {
        if (leg.type === 'ride') {
          for (const s of leg.stops ?? []) stationIds.add(s.stationId)
        } else {
          if (leg.from_station_id !== undefined) stationIds.add(leg.from_station_id)
          if (leg.to_station_id !== undefined) stationIds.add(leg.to_station_id)
        }
      }
      return stationIds
    }
    if (lineViewActive && routeOverlaySchema !== null) {
      return new Set(
        routeOverlaySchema.stations
          .filter((s) => s.line.id === selectedLine.lineId)
          .map((s) => s.id),
      )
    }
    return null
  }, [selectedRoute, lineViewActive, selectedLine, routeOverlaySchema])

  // Auto-fit camera to the route (or line view) bbox whenever a new route /
  // line is shown. Centers the stations inside the visible map area — the
  // area ABOVE the RouteCard / line card panel and right of the sidebar —
  // with 32 css-px padding around the outermost A/B bubbles. Mirrors legacy
  // iOS / Android behaviour where tapping "build route" snaps the viewport
  // to the full route view.
  // Re-run once the first geometry is in: a cold `/lines/<slug>` load
  // selects the line before the canvas has a size to fit into.
  const geometryReady = lightReady || darkReady
  useEffect(() => {
    if (fitStationIds === null || routeOverlaySchema === null || !geometryReady) return
    const canvas = canvasRef.current
    if (canvas === null) return
    if (canvas.width === 0 || canvas.height === 0) return
    const stationIds = fitStationIds
    if (stationIds.size === 0) return
    const stations: Array<{ x: number; y: number }> = []
    for (const station of routeOverlaySchema.stations) {
      if (!stationIds.has(station.id)) continue
      stations.push({ x: station.x, y: station.y })
    }
    if (stations.length === 0) return
    // iPad / desktop sidebar occludes the LEFT of the canvas. Measure
    // its live rect so the auto-fit math centers the route bbox in the
    // VISIBLE area (right of the sidebar). Phone has no sidebar — the
    // selector misses, `leftPanelWidthCss` stays 0, and the math
    // degenerates back to the full-canvas formula.
    let leftPanelWidthCss = 0
    const sidebar = document.querySelector<HTMLElement>('[data-testid="station-details-shell"]')
    if (sidebar !== null) {
      const rect = sidebar.getBoundingClientRect()
      // Right edge of the sidebar is the canvas's effective leading
      // inset — includes the panel's own width AND any leading offset
      // (`left: max(16px, env(safe-area-inset-left))`).
      if (rect.right > 0 && rect.height > 0) {
        leftPanelWidthCss = rect.right
      }
    }
    // Bottom occluded area — the phone route panel / StationCard carousel
    // (measured live) and the canvas part below the visible viewport;
    // `routeFitBottomOcclusionCss` documents which apply in which layout.
    // The canvas is JS-sized to body height, which on iPhone fullscreen
    // Safari spans the full physical screen (TASK-275).
    const cssVarPx = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--mm-body-height'),
    )
    const screenH = typeof window.screen?.height === 'number' ? window.screen.height : 0
    const bottomPanelsCss: number[] = []
    const candidates = document.querySelectorAll<HTMLElement>(
      '[data-testid="route-card-phone"], [data-testid="station-card-overlay"], [data-testid="line-card-overlay"]',
    )
    for (const el of candidates) {
      const rect = el.getBoundingClientRect()
      if (rect.height === 0) continue
      bottomPanelsCss.push(window.innerHeight - rect.top)
    }
    const panelHeightCss = routeFitBottomOcclusionCss({
      hasSidePanel: leftPanelWidthCss > 0,
      bodyGapCss: document.body.clientHeight - window.innerHeight,
      cssVarGapCss: Number.isFinite(cssVarPx) ? cssVarPx - window.innerHeight : 0,
      screenGapCss: window.innerHeight > 0 ? screenH - window.innerHeight : 0,
      bottomPanelsCss,
    })
    const dpr = (window.devicePixelRatio || 1) * (window.visualViewport?.scale ?? 1)
    const fit = computeRouteAutoFit({
      stations,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      dpr,
      panelHeightCss,
      leftPanelWidthCss,
    })
    if (fit === null) return

    cameraAnimatorRef.current.seed(zoomRef.current, offsetXRef.current, offsetYRef.current)
    cameraAnimatorRef.current.setTarget({
      zoom: fit.zoom,
      panX: fit.panX,
      panY: fit.panY,
      nowSec: performance.now() / 1000,
    })
    invalidate()
  }, [fitStationIds, routeOverlaySchema, geometryReady, invalidate])

  useEffect(() => {
    const canvas = canvasRef.current
    if (canvas === null) return

    // §3 `map_gl_init_failed` — WebGL2 context / program init is the
    // first-frame blocker a snapshot test can't catch (the cold-start failure
    // class). Report it before re-throwing so the existing failure handling
    // (error screen) is unchanged — health reporting never alters control flow.
    let renderer: WebGL2Renderer
    try {
      renderer = new WebGL2Renderer(canvas, {
        tileCulling: CITY.mapTileCulling,
        // iPadOS Safari can drop the WebGL2 context under GPU pressure (the
        // full-canvas `backdrop-filter` route side panel is the known
        // trigger). Without recovery the map stays blank white until reload.
        // The renderer rebuilds its own GL resources + re-uploads geometry on
        // restore; these hooks pause / resume the host rAF loop.
        onContextLost: () => {
          contextLostRef.current = true
          if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current)
            rafIdRef.current = null
          }
          // Recoverable GL-health signal (geometry domain). `warning` — the
          // restore path brings the map back without a reload.
          reportHealthError(HEALTH_ID.mapGlInitFailed, 'WebGL2 context lost — awaiting restore', {
            domain: 'geometry',
            severity: 'warning',
            errorKind: 'gl_init',
            extra: { phase: 'context_lost' },
          })
        },
        onContextRestored: () => {
          contextLostRef.current = false
          // The renderer has already rebuilt programs + re-uploaded the
          // retained geometry. Re-apply the host-owned night draw-time filter
          // (cheap + idempotent) and kick the loop so the map repaints.
          const r = rendererRef.current
          if (r !== null) r.setNightVisibleIds(renderNightFilterRef.current)
          needsRenderRef.current = true
          invalidate()
        },
      })
    } catch (err: unknown) {
      reportHealthError(HEALTH_ID.mapGlInitFailed, `WebGL2 init failed: ${String(err)}`, {
        domain: 'geometry',
        severity: 'error',
        errorKind: 'gl_init',
      })
      throw err
    }
    rendererRef.current = renderer
    // TASK-1123: the map UI decides which map bundle this page draws before
    // any map file is read (spec §3.6 `Launch`); later reads reuse it.
    void getMapBundleSession().begin('map')
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void getMapBundleSession().checkForUpdate()
    }
    document.addEventListener('visibilitychange', onVisible)
    const provider = new PrecomputedMmtgProvider()
    let disposed = false

    // Canvas bounding rect, captured lazily (once per gesture / wheel
    // burst) and invalidated on any resize or gesture start.
    // `getBoundingClientRect` forces synchronous layout and was previously
    // paid on EVERY wheel and pinch-move event — layout thrash (Task 1.10).
    // The canvas is viewport-fixed, so its rect only changes when
    // `syncCanvasSize` re-sizes it (all resize paths funnel through there).
    let cachedCanvasRect: DOMRect | null = null
    const canvasRect = (): DOMRect => {
      if (cachedCanvasRect === null) {
        cachedCanvasRect = canvas.getBoundingClientRect()
      }
      return cachedCanvasRect
    }

    const syncCanvasSize = (): void => {
      cachedCanvasRect = null
      // On iOS Safari, `window.innerHeight`, `getBoundingClientRect()`
      // on a `position: fixed; inset: 0` element, and `100dvh` / `100lvh`
      // all return the VISUAL viewport size (the safe area excluding
      // the URL bar), not the physical screen. User-visible TASK-241
      // diagnostic data 2026-04-11: `innerHeight = 714` on a device
      // whose physical screen is ~820 CSS tall.
      //
      // To size the canvas to the full physical screen we take the
      // LARGEST of every candidate metric browsers expose:
      //
      // - `window.innerWidth / innerHeight`
      // - `document.documentElement.clientWidth / clientHeight`
      // - `window.screen.width / height`
      //
      // The canvas then renders into the full screen, Safari overlays
      // its URL bar + status bar as translucent chrome, and the user
      // never sees a gap because there's no "outside the canvas" area.
      // On mobile Safari the OS-level pinch-zoom (on the page, not our
      // gesture-handled canvas) multiplies the CSS → physical ratio
      // beyond `devicePixelRatio` alone. `visualViewport.scale` reports
      // that extra factor. Fold it into the buffer-size DPR so the canvas
      // stays pixel-crisp after a browser-level pinch.
      const vvScale = window.visualViewport?.scale ?? 1
      const dpr = (window.devicePixelRatio || 1) * vvScale
      // iPad in stage manager / split view + Safari ≠ full-screen: the
      // window's CSS area is `innerWidth × innerHeight`, but `screen.*`
      // still reports the FULL device screen (e.g. innerWidth=870 in a
      // stage manager window where screen.width=1024). The TASK-241
      // status-bar workaround below — picking max(innerWidth, screen.*) —
      // would over-size the canvas to 1024, leaving 154 CSS px of canvas
      // outside the Safari window. focusOn() centres the station at
      // canvas-mid = 512, but the Safari window's visible centre is 435,
      // so the station appears ~80 px right of where the user expects.
      // Fixed-position overlays (StationCard) anchor to the layout
      // viewport (= 870) but JS-sized children of #root anchor to the
      // explicit 1024 → card and canvas use different widths and the
      // card visibly clips.
      //
      // Heuristic: `innerWidth >= screen.width` means we ARE full-screen
      // (iPhone Mobile Safari, iPad full-window) and need the iPhone
      // overlay-status-bar fix. `innerWidth < screen.width` means we're
      // in stage manager / split view and the visible Safari window IS
      // the layout viewport — no inflation needed. Without platform
      // detection.
      const { w, h } = pickViewportSize({
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        clientWidth: document.documentElement.clientWidth,
        clientHeight: document.documentElement.clientHeight,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        // Bug 7b: the VISIBLE viewport height is authoritative for the canvas.
        // On iPad Safari the legacy max(innerHeight, clientHeight, screenHeight)
        // heuristic overshoots the visible window (URL-bar overlay), leaving a
        // dark letterbox below the map; `visualViewport.height` is the exact
        // visible height once Safari settles. `undefined` off-Safari falls back
        // to the legacy heuristic (≈ innerHeight on desktop — no regression).
        visualViewportHeight: window.visualViewport?.height,
      })
      // iOS Safari with `body { position: fixed; inset: 0 }` sizes body
      // to the VISUAL viewport (e.g. 402×714 on iPhone 15 Pro) — even
      // with viewport-fit=cover — because the initial containing block
      // excludes the status bar + URL bar. Body's `overflow: hidden`
      // then clips the canvas to body's 714-tall area, leaving visible
      // html background strips at the physical screen top and bottom.
      //
      // Explicitly size body, #root, AND the canvas to the full physical
      // screen so there's no clip boundary between html and canvas.
      // TASK-241 diagnostic overlay confirmed (2026-04-11) that this is
      // the only way to make the canvas visually reach screen edges.
      document.body.style.width = `${w}px`
      document.body.style.height = `${h}px`
      // Expose body's CSS-pixel size to overlays. On iPhone Safari
      // `screen.height` (and thus `h`) exceeds CSS `100lvh` because
      // lvh excludes the status bar / home indicator on some iOS
      // releases. Overlays anchored with `position: fixed` need the
      // ACTUAL body height to compute clearance from the URL bar
      // overlay. See StationCard.module.css `.overlay` and
      // StationCardOrDetailsRouter.module.css `.expandedSidebar`.
      document.documentElement.style.setProperty('--mm-body-height', `${h}px`)
      // Precise Safari bottom-chrome height for `position: fixed`
      // overlays inside the transformed body. visualViewport reports
      // the EXACT visible viewport in CSS px. `index.css` translates
      // `body` up by `env(safe-area-inset-top)` and sizes it to the
      // physical screen, so for elements anchored from body's bottom:
      //
      //   chip_bottom_css = body_bottom_css - visible_viewport_bottom_css
      //                   = (h - insetTop) - (insetTop + vv.height + vv.offsetTop)
      //                   = h - vv.height - vv.offsetTop - 2*insetTop
      //
      // Two cases for `position: fixed` containing block:
      //
      // 1. Body has the index.css `transform: translate(0, -insetTop)`
      //    applied — the transform makes body the containing block, so
      //    `bottom: X` measures from body's bottom edge (= h - insetTop
      //    in CSS coords). The visible viewport bottom in CSS coords is
      //    `insetTop + vv.height + vv.offsetTop`. Diff:
      //    `h - 2 * insetTop - vv.height - vv.offsetTop`.
      //
      // 2. Body has NO transform applied (env(safe-area-inset-top)
      //    resolved to 0 — happens on simulators / form factors without
      //    a notch). Then position:fixed containing block is the LAYOUT
      //    VIEWPORT, which already aligns with the visible viewport on
      //    mobile Safari, so `bottom: 0` is flush against the URL bar.
      //
      // Detect via body's bounding-rect top: < 0 means transformed,
      // ≥ 0 means flat.
      // Safari bottom-chrome (URL bar) height in CSS px.
      //
      // body is JS-sized to the full physical screen so the canvas can
      // bleed under the URL bar. position-fixed children anchored with
      // `bottom: 0` therefore extend BEHIND the URL bar. To place the
      // StationCard / MobileRouteCardOverlay flush with the visible
      // viewport's bottom edge (= top of the URL bar), we expose the
      // chrome height as a CSS var.
      //
      // Universal formula (works for both notch-shifted body and
      // un-shifted iOS 26 Safari):
      //   chrome = body_bottom_y_in_viewport - visible_viewport_bottom_y
      //         = (bodyRectTop + h) - (vv.offsetTop + vv.height)
      //
      // Previous version guarded on `bodyRectTop < 0` (= body transformed
      // up by env(safe-area-inset-top)) and silently returned 0 on iOS 26
      // regular Safari, where env(safe-area-inset-top) is 0 and bodyRectTop
      // stays at 0 — so the URL bar gap was never compensated. TASK-275
      // 2026-05-05.
      const bodyRectTop = document.body.getBoundingClientRect().top
      // index.css applies `transform: translate(0, -env(safe-area-inset-top))`
      // to body on iPhone (<768px). When `safe-area-inset-top` is non-zero
      // the translate shifts body UP. With body height = h (physical screen
      // height), body's physical extent becomes `-insetTop..h-insetTop`,
      // leaving a gap at the physical bottom where the html `body` bg
      // shows through (visible as a coloured strip below the WebGL map).
      // Compensate by extending body / #root / canvas height by the
      // measured translate magnitude (read from bodyRect.top). On iOS 26
      // regular Safari `insetTop` is typically 0-1 px so the compensation
      // is a no-op, but on devices that DO report a meaningful inset
      // this keeps the map edge-to-edge. TASK-275 2026-05-06.
      const insetTopShift = Math.max(0, -bodyRectTop)
      const finalH = h + insetTopShift
      if (insetTopShift > 0) {
        document.body.style.height = `${finalH}px`
        document.documentElement.style.setProperty('--mm-body-height', `${finalH}px`)
      }
      const vv = window.visualViewport
      let safariBottomChrome = 0
      if (vv !== null) {
        const bodyBottomY = bodyRectTop + finalH
        const visibleBottomY = vv.offsetTop + vv.height
        safariBottomChrome = Math.max(0, bodyBottomY - visibleBottomY)
      }
      document.documentElement.style.setProperty(
        '--mm-safari-chrome-bottom',
        `${safariBottomChrome}px`,
      )
      const rootEl = document.getElementById('root')
      if (rootEl !== null) {
        rootEl.style.width = `${w}px`
        rootEl.style.height = `${finalH}px`
      }
      canvas.style.width = `${w}px`
      canvas.style.height = `${finalH}px`
      canvas.width = Math.max(1, Math.floor(w * dpr))
      canvas.height = Math.max(1, Math.floor(finalH * dpr))
    }

    /**
     * Returns `(contain, cover)` zoom factors for the current content bbox
     * vs. viewport size. `contain` = the largest zoom at which the entire
     * bbox fits inside the viewport (like CSS `object-fit: contain`);
     * `cover` = the smallest zoom at which the bbox fills the viewport in
     * both dimensions (like CSS `object-fit: cover`), with the longer
     * content axis overflowing. Falls back to the 4096×4096 canvas box
     * when no bbox has been computed yet.
     */
    const zoomLimits = (): { contain: number; cover: number } => {
      const bbox = contentBBoxRef.current
      const w = bbox !== null ? bbox.maxX - bbox.minX : CANVAS_SIZE
      const h = bbox !== null ? bbox.maxY - bbox.minY : CANVAS_SIZE
      const zx = canvas.width / w
      const zy = canvas.height / h
      return { contain: Math.min(zx, zy), cover: Math.max(zx, zy) }
    }

    /**
     * Initial fit zoom. Uses **cover** mode — the content fills the
     * viewport entirely and the longer dimension overflows and is
     * accessible via pan. This is the only fit that eliminates visible
     * `--bg-color` bands in portrait phone viewports where the content
     * aspect ratio (landscape-ish London Tube map) doesn't match the
     * viewport aspect (phone portrait). See TASK-241 screenshot history
     * for the previous contain-based fit and its failure mode.
     */
    const fitZoom = (): number => zoomLimits().cover

    /**
     * Positions the viewport so the bbox center sits at the viewport
     * center at the chosen cover zoom. Because cover overflows in one
     * direction, part of the content extends beyond the visible area in
     * that direction — the user can pan to see it.
     */
    const recenter = (): void => {
      const override = readInitialViewOverride()
      if (override !== null) {
        zoomRef.current = override.z
        offsetXRef.current = override.x - canvas.width / (2 * override.z)
        offsetYRef.current = override.y - canvas.height / (2 * override.z)
        return
      }
      const z = fitZoom()
      zoomRef.current = z
      const bbox = contentBBoxRef.current
      const cx = bbox !== null ? (bbox.minX + bbox.maxX) / 2 : CANVAS_SIZE / 2
      const cy = bbox !== null ? (bbox.minY + bbox.maxY) / 2 : CANVAS_SIZE / 2
      offsetXRef.current = cx - canvas.width / (2 * z)
      offsetYRef.current = cy - canvas.height / (2 * z)
    }

    const handleResize = (): void => {
      if (disposed) return
      // Preserve the world-space point currently under the viewport center
      // so resize doesn't "teleport" the camera.
      const oldZoom = zoomRef.current
      const oldCenterX = offsetXRef.current + canvas.width / (2 * oldZoom)
      const oldCenterY = offsetYRef.current + canvas.height / (2 * oldZoom)
      syncCanvasSize()
      if (oldZoom === 0) {
        // Hasn't been initialized yet (load still pending) — fit from scratch.
        recenter()
      } else {
        // Keep the same world zoom factor relative to "fit". When the viewport
        // gets bigger, the user sees more; we don't snap back to fitZoom.
        offsetXRef.current = oldCenterX - canvas.width / (2 * oldZoom)
        offsetYRef.current = oldCenterY - canvas.height / (2 * oldZoom)
      }
      invalidate()
    }

    syncCanvasSize()
    // iOS Safari computes visualViewport.height eagerly and may stick at
    // the "URL bar fully expanded" value until the user first interacts —
    // leaving `--mm-safari-chrome-bottom` over-counted and overlays
    // floated 90+ px above the URL bar in cold-load state. After Safari
    // settles the visual-viewport rect post-layout, a deferred re-sync
    // re-reads `visualViewport.height` and re-sizes the canvas to the
    // (now correct) visible height.
    //
    // Bug 7b: the deferred re-sync must run on iPad too. It was previously
    // gated to `< BREAKPOINT_PX` (mobile only), so on iPad (>= 768px) the
    // cold-load overshoot stuck and the map letterboxed below the fold.
    // Run it whenever `visualViewport` exists (phone, iPad, and harmlessly
    // on desktop, where height ≈ innerHeight so the re-sync is a no-op).
    //
    // The 1-px `scrollTo` that forces Safari to recompute the visual
    // viewport stays gated to phone widths: desktop browsers have no URL
    // bar to retract and the nudge flickers sticky content above the fold
    // (TASK-275 2026-05-05; QA finding #19). iPad Safari reports the settled
    // height through the `visualViewport` 'resize' listener wired below
    // without needing the scroll nudge.
    if (window.visualViewport !== null) {
      if (window.innerWidth < BREAKPOINT_PX) {
        window.scrollTo(0, 1)
      }
      setTimeout(syncCanvasSize, 0)
    }

    // ─── Input handlers ──────────────────────────────────────────────────
    //
    // Shared primitive: zoom around a CSS-space anchor point. Used by pinch,
    // double-tap, and wheel so the world point under the anchor stays under
    // the anchor after the zoom changes.
    const zoomAroundCss = (cssX: number, cssY: number, nextZoomRaw: number): void => {
      const z = zoomRef.current
      if (z === 0) return
      const dpr = window.devicePixelRatio || 1
      const rect = canvasRect()
      const anchorPhysicalX = (cssX - rect.left) * dpr
      const anchorPhysicalY = (cssY - rect.top) * dpr
      const worldX = offsetXRef.current + anchorPhysicalX / z
      const worldY = offsetYRef.current + anchorPhysicalY / z
      // Zoom limits derived from the content bbox, not from the current
      // fit zoom — lets the user pinch all the way out to see the whole
      // map (contain × 0.8) and all the way in to cover × 20 for details.
      const limits = zoomLimits()
      const minZ = limits.contain * MIN_ZOOM_CONTAIN_FACTOR
      const maxZ = limits.cover * MAX_ZOOM_COVER_FACTOR
      const nextZoom = Math.max(minZ, Math.min(maxZ, nextZoomRaw))
      offsetXRef.current = worldX - anchorPhysicalX / nextZoom
      offsetYRef.current = worldY - anchorPhysicalY / nextZoom
      zoomRef.current = nextZoom
    }

    // Multi-pointer tracking: 1 pointer = pan, 2 pointers = pinch.
    // Third+ pointers are tracked but don't change the gesture state.
    const activePointers = new Map<number, { cssX: number; cssY: number }>()

    // Pan gesture state (active when exactly 1 pointer is down).
    let panStartCssX = 0
    let panStartCssY = 0
    let panStartOffsetX = 0
    let panStartOffsetY = 0

    // Pan velocity sampling — last N ms of physical-pixel pointer positions.
    // Used at pan-release to compute the initial momentum velocity as a
    // time-averaged rate rather than the raw last-event delta (which is
    // noisy when pointer events bunch up at the tail of a drag).
    let panSamples: Array<{ t: number; xPhysical: number; yPhysical: number }> = []

    // Momentum state — populated at pan-release, drained on rAF ticks.
    // Stored in physical-px-per-second for frame-rate independence; each
    // tick converts to a per-frame delta using the rAF timestamp delta.
    let momentumRafId: number | null = null
    let momentumVelocityXPerSec = 0
    let momentumVelocityYPerSec = 0
    let momentumPrevTime = 0

    const cancelMomentum = (): void => {
      if (momentumRafId !== null) {
        cancelAnimationFrame(momentumRafId)
        momentumRafId = null
      }
      momentumVelocityXPerSec = 0
      momentumVelocityYPerSec = 0
      momentumPrevTime = 0
    }

    const momentumStep = (now: DOMHighResTimeStamp): void => {
      momentumRafId = null
      const z = zoomRef.current
      if (z === 0) {
        cancelMomentum()
        return
      }

      const dtSec: number = momentumPrevTime > 0 ? (now - momentumPrevTime) / 1000 : 1 / 60
      momentumPrevTime = now

      // Sign convention matches the onPointerMove pan branch:
      //   offsetX = panStartOffsetX - dxPhysical / z
      // Velocity has the same sign as dxPhysical, so momentum applies
      //   offsetX -= velocity * dt / z
      // to continue the same visual drift.
      offsetXRef.current -= (momentumVelocityXPerSec * dtSec) / z
      offsetYRef.current -= (momentumVelocityYPerSec * dtSec) / z

      // Exponential decay: `velocity(t) = v0 * friction^t` with `t` in
      // seconds. Computing `friction^dtSec` each frame gives the same
      // total decay regardless of frame rate (60 Hz monitor vs 120 Hz
      // ProMotion both see the identical decay curve).
      const decay = Math.pow(MOMENTUM_FRICTION_PER_SEC, dtSec)
      momentumVelocityXPerSec *= decay
      momentumVelocityYPerSec *= decay

      invalidate()

      if (
        Math.abs(momentumVelocityXPerSec) > MOMENTUM_STOP_VELOCITY_PX_PER_SEC ||
        Math.abs(momentumVelocityYPerSec) > MOMENTUM_STOP_VELOCITY_PX_PER_SEC
      ) {
        momentumRafId = requestAnimationFrame(momentumStep)
      } else {
        momentumVelocityXPerSec = 0
        momentumVelocityYPerSec = 0
        momentumPrevTime = 0
      }
    }

    const computeReleaseVelocity = (): { vxPerSec: number; vyPerSec: number } => {
      // Need at least two samples within the sliding window to compute a
      // delta. A single sample (or none) means the pointer barely moved —
      // no momentum.
      if (panSamples.length < 2) return { vxPerSec: 0, vyPerSec: 0 }
      const first = panSamples[0]
      const last = panSamples[panSamples.length - 1]
      const dtMs = last.t - first.t
      if (dtMs <= 0) return { vxPerSec: 0, vyPerSec: 0 }
      // Δpx / Δms → physical px / ms, × 1000 → physical px / second.
      const vxPerSec = ((last.xPhysical - first.xPhysical) / dtMs) * 1000
      const vyPerSec = ((last.yPhysical - first.yPhysical) / dtMs) * 1000
      return { vxPerSec, vyPerSec }
    }

    // Pinch gesture state (active when exactly 2 pointers are down).
    // Captured at the moment pointer count reaches 2; `resetPinchFromCurrent`
    // re-captures it so lifting/adding a finger doesn't snap the view.
    let pinching = false
    let pinchStartDistanceCss = 0
    let pinchStartZoom = 0
    let pinchStartCenterCssX = 0
    let pinchStartCenterCssY = 0
    let pinchStartOffsetX = 0
    let pinchStartOffsetY = 0

    // Double-tap detection (touch only — trackpad/mouse have wheel zoom).
    let lastTapTimeMs = 0
    let lastTapCssX = 0
    let lastTapCssY = 0

    const getTwoPointers = (): [{ cssX: number; cssY: number }, { cssX: number; cssY: number }] => {
      const it = activePointers.values()
      const a = it.next().value as { cssX: number; cssY: number }
      const b = it.next().value as { cssX: number; cssY: number }
      return [a, b]
    }

    const startPinchFromCurrent = (): void => {
      const [a, b] = getTwoPointers()
      const dx = b.cssX - a.cssX
      const dy = b.cssY - a.cssY
      pinchStartDistanceCss = Math.hypot(dx, dy)
      pinchStartZoom = zoomRef.current
      pinchStartCenterCssX = (a.cssX + b.cssX) / 2
      pinchStartCenterCssY = (a.cssY + b.cssY) / 2
      pinchStartOffsetX = offsetXRef.current
      pinchStartOffsetY = offsetYRef.current
      pinching = true
    }

    const startPanFromCurrent = (p: { cssX: number; cssY: number }): void => {
      panStartCssX = p.cssX
      panStartCssY = p.cssY
      panStartOffsetX = offsetXRef.current
      panStartOffsetY = offsetYRef.current
    }

    const onPointerDown = (e: PointerEvent): void => {
      if (e.pointerType === 'mouse' && e.button !== 0) return

      // Fresh gesture — re-capture the canvas rect once; the per-move
      // pinch math then reads the cached value instead of forcing layout.
      cachedCanvasRect = null

      // Any new pointer interaction cancels an ongoing momentum glide so
      // the user can "catch" a moving map — iOS UIScrollView default.
      cancelMomentum()

      // TASK-238.3: cancel the focus spring before the gesture handler
      // writes to the refs — otherwise the spring and the gesture fight.
      cameraAnimatorRef.current.cancel()

      // Double-tap detection (touch only). Must run *before* we mutate
      // activePointers so a tap after a previous tap is caught on the
      // down-edge of the second tap.
      if (e.pointerType === 'touch' && activePointers.size === 0) {
        const now = performance.now()
        const dt = now - lastTapTimeMs
        const dx = e.clientX - lastTapCssX
        const dy = e.clientY - lastTapCssY
        if (dt < DOUBLE_TAP_MAX_DELAY_MS && Math.hypot(dx, dy) < DOUBLE_TAP_MAX_DISTANCE_PX) {
          const z = zoomRef.current
          if (z !== 0) {
            zoomAroundCss(e.clientX, e.clientY, z * DOUBLE_TAP_ZOOM_FACTOR)
            invalidate()
          }
          // Consume the tap so triple-tap doesn't keep zooming.
          lastTapTimeMs = 0
          // Cancel the pending single-tap dispatch from the first tap
          // of this double-tap sequence — user is zooming, not selecting.
          if (pendingTapDispatchRef.current !== null) {
            clearTimeout(pendingTapDispatchRef.current)
            pendingTapDispatchRef.current = null
          }
          tapCandidateRef.current = null
          // Do not start a pan for this down event — it's part of the zoom gesture.
          return
        }
        lastTapTimeMs = now
        lastTapCssX = e.clientX
        lastTapCssY = e.clientY
      }

      canvas.setPointerCapture(e.pointerId)
      activePointers.set(e.pointerId, { cssX: e.clientX, cssY: e.clientY })

      // Start a single-tap candidate for every single-pointer down —
      // mouse, touch, pen. It will be cleared by a move beyond threshold,
      // by the double-tap-zoom branch above, or consumed in `endPointer`.
      if (activePointers.size === 1) {
        tapCandidateRef.current = {
          cssX: e.clientX,
          cssY: e.clientY,
          t: performance.now(),
          pointerId: e.pointerId,
        }
      } else {
        // Multi-touch is not a tap.
        tapCandidateRef.current = null
      }

      if (activePointers.size === 1) {
        pinching = false
        startPanFromCurrent({ cssX: e.clientX, cssY: e.clientY })
        // Seed the velocity sample buffer with the starting position so the
        // first pointermove already has a reference for delta computation.
        const dpr = window.devicePixelRatio || 1
        panSamples = [
          {
            t: performance.now(),
            xPhysical: e.clientX * dpr,
            yPhysical: e.clientY * dpr,
          },
        ]
      } else if (activePointers.size === 2) {
        startPinchFromCurrent()
        // Dropping into a pinch kills any pan sampling — momentum only
        // applies to single-pointer pans, not to pinch-pan hybrids.
        panSamples = []
      }
      // 3+ pointers: keep current gesture state; ignore the extra pointer.
    }

    const onPointerMove = (e: PointerEvent): void => {
      if (!activePointers.has(e.pointerId)) return
      activePointers.set(e.pointerId, { cssX: e.clientX, cssY: e.clientY })

      // Kill the single-tap candidate once the finger has moved beyond
      // the drag threshold — this is a pan, not a tap.
      const cand = tapCandidateRef.current
      if (cand !== null && cand.pointerId === e.pointerId) {
        const dx = e.clientX - cand.cssX
        const dy = e.clientY - cand.cssY
        if (dx * dx + dy * dy > TAP_MAX_MOVEMENT_PX * TAP_MAX_MOVEMENT_PX) {
          tapCandidateRef.current = null
        }
      }

      const z = zoomRef.current
      if (z === 0) return

      if (pinching && activePointers.size >= 2) {
        const [a, b] = getTwoPointers()
        const dx = b.cssX - a.cssX
        const dy = b.cssY - a.cssY
        const currentDistance = Math.hypot(dx, dy)
        if (pinchStartDistanceCss < 1) return

        // Scale pinned to the initial pinch state, not accumulated frame-by-frame.
        const scale = currentDistance / pinchStartDistanceCss
        const limits = zoomLimits()
        const minZ = limits.contain * MIN_ZOOM_CONTAIN_FACTOR
        const maxZ = limits.cover * MAX_ZOOM_COVER_FACTOR
        const nextZoom = Math.max(minZ, Math.min(maxZ, pinchStartZoom * scale))

        // Pan component: world point under the *initial* pinch center should
        // now be under the *current* pinch center.
        const dpr = window.devicePixelRatio || 1
        const rect = canvasRect()
        const startCenterPhysicalX = (pinchStartCenterCssX - rect.left) * dpr
        const startCenterPhysicalY = (pinchStartCenterCssY - rect.top) * dpr
        const worldX = pinchStartOffsetX + startCenterPhysicalX / pinchStartZoom
        const worldY = pinchStartOffsetY + startCenterPhysicalY / pinchStartZoom

        const currentCenterCssX = (a.cssX + b.cssX) / 2
        const currentCenterCssY = (a.cssY + b.cssY) / 2
        const currentCenterPhysicalX = (currentCenterCssX - rect.left) * dpr
        const currentCenterPhysicalY = (currentCenterCssY - rect.top) * dpr

        offsetXRef.current = worldX - currentCenterPhysicalX / nextZoom
        offsetYRef.current = worldY - currentCenterPhysicalY / nextZoom
        zoomRef.current = nextZoom
        invalidate()
      } else if (activePointers.size === 1) {
        const dpr = window.devicePixelRatio || 1
        const dxPhysical = (e.clientX - panStartCssX) * dpr
        const dyPhysical = (e.clientY - panStartCssY) * dpr
        offsetXRef.current = panStartOffsetX - dxPhysical / z
        offsetYRef.current = panStartOffsetY - dyPhysical / z

        // Append a velocity sample and trim anything older than the window.
        // `panSamples` is always append-ordered; binary search is overkill
        // for ~6 samples at 60 Hz × 100 ms window.
        const now = performance.now()
        panSamples.push({
          t: now,
          xPhysical: e.clientX * dpr,
          yPhysical: e.clientY * dpr,
        })
        const cutoff = now - PAN_VELOCITY_WINDOW_MS
        while (panSamples.length > 0 && panSamples[0].t < cutoff) {
          panSamples.shift()
        }

        invalidate()
      }
    }

    const endPointer = (e: PointerEvent): void => {
      if (!activePointers.has(e.pointerId)) return
      const wasPinching = pinching
      activePointers.delete(e.pointerId)
      if (canvas.hasPointerCapture(e.pointerId)) {
        canvas.releasePointerCapture(e.pointerId)
      }

      if (activePointers.size === 1) {
        // Was pinching with 2; one finger remains. Reset pan origin to the
        // remaining pointer so continuing drag is smooth (no snap).
        pinching = false
        const [remaining] = activePointers.values()
        startPanFromCurrent(remaining)
        // Re-seed the velocity buffer from the new anchor — the residual
        // samples from the pinch-pan are mismatched in origin and would
        // produce a wrong momentum vector.
        const dpr = window.devicePixelRatio || 1
        panSamples = [
          {
            t: performance.now(),
            xPhysical: remaining.cssX * dpr,
            yPhysical: remaining.cssY * dpr,
          },
        ]
      } else if (activePointers.size === 0) {
        // Last pointer lifted — if this was a single-pointer pan (not the
        // tail of a pinch), start a momentum glide from the recent samples.
        // Pinches don't get momentum: the release velocity of a pinch is
        // usually the user carefully positioning, not a directional fling.
        if (!wasPinching) {
          const { vxPerSec, vyPerSec } = computeReleaseVelocity()
          if (
            Math.abs(vxPerSec) > MOMENTUM_STOP_VELOCITY_PX_PER_SEC ||
            Math.abs(vyPerSec) > MOMENTUM_STOP_VELOCITY_PX_PER_SEC
          ) {
            momentumVelocityXPerSec = vxPerSec
            momentumVelocityYPerSec = vyPerSec
            momentumPrevTime = 0
            if (momentumRafId === null) {
              momentumRafId = requestAnimationFrame(momentumStep)
            }
          }
        }
        pinching = false
        panSamples = []
      } else if (activePointers.size >= 2 && pinching) {
        // Was 3+, now 2 — re-anchor the pinch to the current two pointers.
        startPinchFromCurrent()
      }

      // Single-tap dispatch path. Ignore if the candidate was cleared by
      // a drag past the threshold. Touch taps defer by
      // DOUBLE_TAP_MAX_DELAY_MS so a pending double-tap-zoom can cancel
      // them in `onPointerDown`; mouse/pen dispatch immediately.
      const cand = tapCandidateRef.current
      tapCandidateRef.current = null
      if (cand !== null && cand.pointerId === e.pointerId) {
        const dt = performance.now() - cand.t
        if (dt < TAP_MAX_DURATION_MS) {
          const fireTap = (): void => {
            const canvasEl = canvasRef.current
            const zLocal = zoomRef.current
            if (canvasEl === null || zLocal <= 0) return
            const rect = canvasEl.getBoundingClientRect()
            const dpr = window.devicePixelRatio || 1
            // Transform convention: `offsetX` is unscaled canvas-space
            // (see onPointerMove pan: `offsetX = panStart - dx / zoom`,
            //  and zoomAroundCss / pinch math using
            //  `worldX = offsetX + physicalX / zoom`). Inverse for a tap
            // at (clientX, clientY) is `canvas = offsetX + (css - rect) * dpr / zoom`.
            const canvasX = offsetXRef.current + ((cand.cssX - rect.left) * dpr) / zLocal
            const canvasY = offsetYRef.current + ((cand.cssY - rect.top) * dpr) / zLocal
            // §13: a map tap while Suggester is open in the tablet/
            // desktop side panel dismisses the suggester. Empty-vs-station
            // is decided by the `MapTapped` reducer below — for an empty
            // tap `selectedStation` stays/becomes null (panel → RouteCard,
            // pills retained), for a station it is selected (panel →
            // StationDetails once `?suggester=` is gone). The map tap
            // never fills the active pill (route-build uses the list).
            // `dismissSuggester` is forwarded into the event so the reducer
            // bypasses its route-active block — the suggester can be
            // reopened over a built route and the §13 station-tap /
            // empty-tap must still fire (parity with Android).
            const dismissSuggester = shouldDismissSuggesterOnMapTap({
              suggesterSlot: suggesterSlotRef.current,
              isExpanded: isDesktopViewportRef.current,
            })
            if (dismissSuggester) {
              closeSuggesterRef.current()
            }
            dispatch({
              kind: 'MapTapped',
              canvasX,
              canvasY,
              stationsByTapGrid: buildStationLookup(
                schemaRef.current,
                store.getState().currentMode,
              ),
              dismissSuggester,
              // D: hit rejection. Non-null in night mode and, in a city
              // with night-only stops, by day; the reducer rejects a hit on
              // a station absent from this drawn set so a hidden station
              // can't be selected. `?? undefined` keeps the field omitted
              // when everything is drawn (no rejection).
              nightStationIds: modeVisibleIdsRef.current ?? undefined,
              // Line view: the line segment under the tap (12 css-px reach
              // around the drawn line). The reducer uses it only when no
              // station is hit.
              segmentLine:
                lineSegmentHitTesterRef.current?.(
                  canvasX,
                  canvasY,
                  (LINE_SEGMENT_TAP_REACH_CSS_PX * dpr) / zLocal,
                  modeVisibleIdsRef.current,
                ) ?? undefined,
            })
          }
          if (e.pointerType === 'touch') {
            // Debounce against a potential double-tap-zoom. If a second
            // pointerdown arrives in the window, the double-tap branch
            // in `onPointerDown` clears `pendingTapDispatchRef`.
            if (pendingTapDispatchRef.current !== null) {
              clearTimeout(pendingTapDispatchRef.current)
            }
            pendingTapDispatchRef.current = window.setTimeout(() => {
              pendingTapDispatchRef.current = null
              fireTap()
            }, DOUBLE_TAP_MAX_DELAY_MS)
          } else {
            fireTap()
          }
        }
      }
    }

    const onWheel = (e: WheelEvent): void => {
      e.preventDefault()
      // Wheel events (zoom or trackpad pan) interrupt momentum — the user
      // is taking direct control of the viewport and shouldn't fight
      // residual inertia from the previous gesture.
      cancelMomentum()
      const z = zoomRef.current
      if (z === 0) return

      // Classify the wheel event. On macOS trackpads, browsers set `ctrlKey`
      // for pinch gestures even without the physical Ctrl key — that's the
      // reliable pinch marker. Everything else is either a mouse wheel notch
      // (large integer deltaY or deltaMode != 0) or a two-finger trackpad
      // scroll (small fractional deltaY with deltaMode === 0).
      const isPinchZoom = e.ctrlKey
      const isMouseWheel = !isPinchZoom && (e.deltaMode !== 0 || Math.abs(e.deltaY) >= 50)
      const shouldZoom = isPinchZoom || isMouseWheel

      if (shouldZoom) {
        // Normalize deltaY to pixel units (Firefox uses LINE/PAGE modes on
        // some mice). Values taken from the WHATWG reference implementation.
        const normalizedDeltaY =
          e.deltaMode === 1 ? e.deltaY * 100 : e.deltaMode === 2 ? e.deltaY * 800 : e.deltaY
        const sensitivity = isPinchZoom ? PINCH_ZOOM_SENSITIVITY : WHEEL_ZOOM_SENSITIVITY
        const factor = Math.exp(-normalizedDeltaY * sensitivity)
        zoomAroundCss(e.clientX, e.clientY, z * factor)
      } else {
        // Trackpad two-finger scroll: pan by the wheel delta.
        const dpr = window.devicePixelRatio || 1
        offsetXRef.current += (e.deltaX * dpr) / z
        offsetYRef.current += (e.deltaY * dpr) / z
      }
      invalidate()
    }

    canvas.addEventListener('pointerdown', onPointerDown)
    canvas.addEventListener('pointermove', onPointerMove)
    canvas.addEventListener('pointerup', endPointer)
    canvas.addEventListener('pointercancel', endPointer)
    canvas.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('resize', handleResize)
    // On iOS Safari the URL bar retracts/extends as the user scrolls
    // near the edges — that changes the visible viewport size WITHOUT
    // firing a `window` resize event. The `visualViewport` API is the
    // only reliable way to hear about it.
    window.visualViewport?.addEventListener('resize', handleResize)
    // Also listen to visualViewport.scroll — fires during browser-level
    // pinch-zoom gestures (scale changes without CSS size changes).
    // Without this, a pinch leaves the canvas buffer at the old DPR
    // and renders pixelated until the user triggers another resize.
    window.visualViewport?.addEventListener('scroll', handleResize)

    // ─── Geometry load ───────────────────────────────────────────────────
    // Load the matching theme first so dark-system users see the correct
    // palette on cold start; preload the other theme in the background.
    const initialTheme = themeRef.current
    const otherTheme: Theme = initialTheme === 'dark' ? 'light' : 'dark'

    // Schema is needed only for tap-resolution (stations + tapGrids).
    // Fetch in parallel with geometry — the WebGL pipeline does not
    // depend on it, so render start is not gated on this.
    fetchSchema()
      .then((schema) => {
        if (disposed) return
        schemaRef.current = schema
      })
      .catch((err: unknown) => {
        console.error('MetroMapCanvas: failed to load schema.json', err)
        // §3 `schema_decode_failed` — station/line schema decode failed.
        // Tap-resolution degrades (the map still renders), so `warning`.
        // Canon (iOS MetroApp.swift + Android MetroMapViewModel.kt): the
        // on-device schema decode is a ROUTING-data failure, not a network
        // one → `{ domain: 'routing', error_kind: 'decode' }` with NO
        // `endpoint` (AppMetrica/Amplitude group by domain+id, so this is
        // what lands web in the same bucket as mobile).
        reportHealthError(HEALTH_ID.schemaDecodeFailed, `schema.json load failed: ${String(err)}`, {
          domain: 'routing',
          severity: 'warning',
          errorKind: 'decode',
        })
      })

    provider
      .load(initialTheme)
      .then((primarySet) => {
        if (disposed) return
        if (initialTheme === 'dark') {
          darkSetRef.current = primarySet
        } else {
          lightSetRef.current = primarySet
        }
        // Compute the tight content bbox so `fitZoom` / `recenter` /
        // `zoomLimits` can cover the viewport with real content rather
        // than the 4096×4096 canvas (which has significant letterboxing
        // around the London Tube content). Content is the same across
        // themes, so only the first-loaded set needs to be scanned.
        contentBBoxRef.current = computeContentBBox(primarySet)
        renderer.uploadGeometry(primarySet)
        recenter()
        // Deep-link hydration (e.g. the station page's "Interactive
        // map" → /stations/<slug>?from=): the selection is seeded into
        // the store BEFORE the canvas has a size, so the
        // focus-on-selection effect no-oped on its `currentZoom <= 0`
        // guard and the cover-fit above would leave the station
        // off-centre — or under the desktop sidebar. Now that the
        // camera exists, focus it like the tap would have, minus the
        // spring (cold-load recenter rule §3.2).
        const preselected = store.getState().selectedStation
        if (preselected !== null) {
          focusOnRef.current(preselected, { instant: true })
        }
        dispatch({ kind: 'GeometryReady', theme: initialTheme })
        invalidate()

        // Background-preload the other theme; failures don't block interaction.
        provider
          .load(otherTheme)
          .then((otherSet) => {
            if (disposed) return
            if (otherTheme === 'dark') {
              darkSetRef.current = otherSet
            } else {
              lightSetRef.current = otherSet
            }
            dispatch({ kind: 'GeometryReady', theme: otherTheme })
          })
          .catch((err: unknown) => {
            console.error(`MetroMapCanvas: failed to preload ${otherTheme} theme`, err)
            // §3 `mmtg_load_failed` — background preload of the OTHER theme
            // failed. The active theme already rendered, so this is a
            // degraded-state `warning` (the theme swap will retry on demand),
            // not a render blocker.
            const { errorKind, statusCode } = classifyNetworkError(err)
            reportHealthError(
              HEALTH_ID.mmtgLoadFailed,
              `mmtg preload (${otherTheme}) failed: ${String(err)}`,
              { domain: 'geometry', severity: 'warning', errorKind, statusCode },
            )
          })
      })
      .catch((err: unknown) => {
        console.error(`MetroMapCanvas: failed to load ${initialTheme} geometry`, err)
        // §3 `mmtg_load_failed` — the ACTIVE theme's geometry failed to
        // load/decode → the map cannot render (first-frame blocker), so
        // `error`. P0: the snapshot-invisible map-render failure.
        const { errorKind, statusCode } = classifyNetworkError(err)
        reportHealthError(
          HEALTH_ID.mmtgLoadFailed,
          `mmtg load (${initialTheme}) failed: ${String(err)}`,
          { domain: 'geometry', severity: 'error', errorKind, statusCode },
        )
      })

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisible)
      canvas.removeEventListener('pointerdown', onPointerDown)
      canvas.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('pointerup', endPointer)
      canvas.removeEventListener('pointercancel', endPointer)
      canvas.removeEventListener('wheel', onWheel)
      window.removeEventListener('resize', handleResize)
      window.visualViewport?.removeEventListener('resize', handleResize)
      window.visualViewport?.removeEventListener('scroll', handleResize)
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
      if (momentumRafId !== null) {
        cancelAnimationFrame(momentumRafId)
        momentumRafId = null
      }
      renderer.dispose()
      rendererRef.current = null
      lightSetRef.current = null
      darkSetRef.current = null
      if (pendingTapDispatchRef.current !== null) {
        clearTimeout(pendingTapDispatchRef.current)
        pendingTapDispatchRef.current = null
      }
      tapCandidateRef.current = null
      schemaRef.current = null
    }
  }, [invalidate, dispatch, store])

  // Loader shows until the **currently active** theme's GeometrySet is ready.
  // A dark-system user shouldn't block on light.
  const currentThemeReady = effectiveTheme === 'dark' ? darkReady : lightReady

  return (
    <>
      <canvas ref={canvasRef} className="metro-map-canvas" />
      {currentThemeReady && (
        <StationLabelsOverlay
          ref={labelsOverlayRef}
          // M: labels follow the EFFECTIVE theme (night → dark set) and, in
          // night mode, the pre-filtered night label variant so non-night
          // station + line-name labels disappear with the night map filter.
          theme={effectiveTheme}
          // TASK-1136: while a late-night frame is still loading the day
          // geometry is on screen, so its labels are too (the night label
          // set of such a bundle is drawn from the night frame).
          nightMode={currentMode === 'night' && nightFrame.status !== 'loading'}
          onReady={invalidate}
        />
      )}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: dimBackgroundColor,
          opacity: dimTargetOpacity,
          // 500ms matches the settle time of the iOS / Android
          // DimAlphaAnimator (ω=10, ~500ms to within SETTLE_THRESHOLD).
          // FastOutSlowInEasing is the Material-standard curve Android
          // uses; the CSS `cubic-bezier` equivalent is (0.4, 0, 0.2, 1).
          transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: 'none',
          // z=2 sits between StationLabelsOverlay (z=1) and
          // BubbleOverlay host (z=3) — map → text → dim → bubble,
          // matching the engine-rendered layering on iOS/Android.
          zIndex: 2,
          willChange: 'opacity',
        }}
      />
      {/*
       * RouteHighlightOverlay sits at z=3, above the dim layer (z=2)
       * but below the BubbleOverlay host (z=4+). Renders the selected
       * route's connections / transitions / station glyphs / station
       * texts as inline SVG, replicating the legacy iOS / Android
       * layering: dimmed map below, highlighted route + texts above.
       *
       * Schema is the source of truth for `svgPath` strings — including
       * `skipInitialDraw` transitions and texts that the cold-start
       * MMTG load skips.
       */}
      <RouteHighlightOverlay
        ref={routeHighlightOverlayRef}
        highlightedIds={highlightedElementIds}
        schema={routeOverlaySchema}
        theme={theme}
        nightFrame={drawNightFrame}
      />
      <BubbleOverlay
        ref={bubbleOverlayRef}
        selected={selectedStation}
        // Hint label: "A" if next pick fills the from slot, "B" once
        // from is already chosen.
        text={routeSelection.fromStation !== null ? 'B' : 'A'}
        fillColorForLine={(lineId) => hexColorFromSchemaLineId(schemaRef.current, lineId, theme)}
        getTransform={() => ({
          zoom: zoomRef.current,
          panX: offsetXRef.current,
          panY: offsetYRef.current,
          // Bug 5: use the same effective DPR as syncCanvasSize so the
          // bubble's mount-time positioning lines up with the canvas
          // viewport even when a browser-level pinch has bumped
          // visualViewport.scale above 1.
          dpr: (window.devicePixelRatio || 1) * (window.visualViewport?.scale ?? 1),
        })}
      />
      <RouteBubbleOverlay
        ref={routeBubbleOverlayRef}
        fromStation={routeFrom}
        toStation={routeTo}
        // A/B bubble fill = the station's line colour. Resolved through
        // the SAME reactive, theme-aware path the RouteCard endpoint
        // chip uses (`lineHexColorFromId` against the reactive
        // `routeOverlaySchema` from `useSchema()`) so the map pin and the
        // search-field pill are guaranteed to render the identical hex in
        // both themes — the single line-colour source (`lineHexForTheme`
        // / `colorModes[theme]`).
        //
        // Bug 1 (cold-start / cache-restore desync): the previous
        // resolver read the NON-reactive `schemaRef.current` and the flat
        // legacy `hexColor`. When a cached route selection hydrated before
        // the schema finished loading, the bubble resolved grey and never
        // re-rendered once the schema arrived (a ref mutation triggers no
        // React update), so the pin stayed grey while the pill went red.
        // Reading the reactive schema re-renders this overlay the moment
        // the schema resolves, flipping grey → the real line colour.
        // Returns null while the schema is unloaded or the line id is
        // unknown → RouteBubbleOverlay applies its `#1C1C1C` last-resort
        // fallback (kept legible for the white A/B letter).
        getLineColor={(lineId) =>
          routeOverlaySchema === null ? null : lineHexColorFromId(routeOverlaySchema, lineId, theme)
        }
        getTransform={() => ({
          zoom: zoomRef.current,
          panX: offsetXRef.current,
          panY: offsetYRef.current,
          dpr: (window.devicePixelRatio || 1) * (window.visualViewport?.scale ?? 1),
        })}
      />
      {/* Walk-segment overlay sits between the dim layer (z=2) and the
          A/B route bubbles (z=4) — z=3, same band as RouteHighlightOverlay
          so the walking dots layer on top of dimmed map glyphs but stay
          underneath the bubble pills. Skips internally when the selected
          route has no walk legs or no route is selected. */}
      {routeOverlaySchema !== null && selectedRoute !== null ? (
        <WalkSegmentOverlay
          ref={walkSegmentOverlayRef}
          route={selectedRoute}
          schema={routeOverlaySchema}
          highlightedIds={highlightedElementIds}
          getTransform={() => ({
            zoom: zoomRef.current,
            panX: offsetXRef.current,
            panY: offsetYRef.current,
            dpr: (window.devicePixelRatio || 1) * (window.visualViewport?.scale ?? 1),
          })}
        />
      ) : null}
      <DesktopStationActionsPopup
        ref={desktopStationActionsPopupRef}
        selectedStation={selectedStation}
        enabled={desktopActionsEnabled}
        // Tapping From / To on the popup mirrors the mobile
        // StationCard carousel pattern: dispatch the route slot
        // selection, then clear the station selection so the bubble
        // (and popup) fade out cleanly. Equivalent to dismissing the
        // carousel on mobile.
        onPickFrom={(station) => {
          dispatch({ kind: 'RouteFromSelected', station })
          dispatch({ kind: 'StationSelected', snapshot: null })
        }}
        onPickTo={(station) => {
          dispatch({ kind: 'RouteToSelected', station })
          dispatch({ kind: 'StationSelected', snapshot: null })
        }}
        getTransform={() => ({
          zoom: zoomRef.current,
          panX: offsetXRef.current,
          panY: offsetYRef.current,
          dpr: (window.devicePixelRatio || 1) * (window.visualViewport?.scale ?? 1),
        })}
      />
      <UserLocationDotOverlay
        ref={userLocationDotOverlayRef}
        target={userLocationDotTarget}
        getTransform={() => ({
          zoom: zoomRef.current,
          panX: offsetXRef.current,
          panY: offsetYRef.current,
          dpr: (window.devicePixelRatio || 1) * (window.visualViewport?.scale ?? 1),
        })}
      />
      {!currentThemeReady && <Loader />}
    </>
  )
}
/* v8 ignore stop */
